/**
 * Ultimate Scope template helpers — resolve options_ref, groups, when, wizard steps.
 * Template shape: GET /interior/scope-templates/ultimate → { template }
 */
import { emptyTipTapNotes } from './tipTapEmpty';
import {
  COMMON_ANSWER_NUMBER_IDS,
  PROGRAM_NUMBER_IDS,
  SINGLE_SELECT_ANSWER_IDS,
  coerceAnswerNumberValue,
  coerceAnswerStrings,
  coerceToApiNumber,
  coerceToApiString,
  isApiNumberFieldId,
  isEmptyAnswerNumber,
  isStringNotNumberFieldId,
  looksLikeOptionLabel,
  normalizeFtSpacing,
} from './scopeNumberCoercion';

/** Normalize option labels to template contract (e.g. "8ft" → "8 ft"). */
export const normalizeOptionLabel = (opt) => normalizeFtSpacing(opt ?? '');

/**
 * Map a stored / UI value onto an exact allowed option string.
 * Handles casing, "8ft" vs "8 ft", and short prefixes ("Basic", "Mid").
 */
export const canonicalizeOptionValue = (value, allowedSet) => {
  if (value == null || value === '') return null;
  if (!allowedSet || allowedSet.size === 0) return null;
  const allowed = [...allowedSet].map((o) => String(o));
  const raw = normalizeOptionLabel(value);
  if (!raw) return null;

  if (allowedSet.has(raw) || allowed.includes(raw)) {
    return allowed.find((o) => o === raw) || raw;
  }

  const lower = raw.toLowerCase();
  const ci = allowed.find((o) => o.toLowerCase() === lower);
  if (ci) return ci;

  const norm = (s) =>
    normalizeOptionLabel(s)
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .trim();
  const rawN = norm(raw);
  const byNorm = allowed.find((o) => norm(o) === rawN);
  if (byNorm) return byNorm;

  const slug = (s) => norm(s).replace(/[^a-z0-9]+/g, '');
  const rawSlug = slug(raw);
  if (rawSlug.length >= 3) {
    const bySlug = allowed.find((o) => slug(o) === rawSlug);
    if (bySlug) return bySlug;
    const byPrefix = allowed.find((o) => {
      const os = slug(o);
      return os.startsWith(rawSlug) || rawSlug.startsWith(os.slice(0, 8));
    });
    if (byPrefix) return byPrefix;
  }

  // First-token match: "Basic" → "Basic (Local Indian Brands)"
  const firstToken = rawN.split(/[\s(/]+/)[0];
  if (firstToken && firstToken.length >= 3) {
    const matches = allowed.filter(
      (o) => norm(o).split(/[\s(/]+/)[0] === firstToken
    );
    if (matches.length === 1) return matches[0];
  }

  return null;
};

const isSingleSelectAnswerId = (id = '') => {
  const key = String(id || '').toLowerCase();
  return (
    SINGLE_SELECT_ANSWER_IDS.has(key) ||
    SINGLE_SELECT_ANSWER_IDS.has(String(id || '')) ||
    key.endsWith('_hardware_level') ||
    key === 'hardware'
  );
};

export {
  coerceToApiNumber,
  isApiNumberFieldId,
  isStringNotNumberFieldId,
  PROGRAM_NUMBER_IDS,
} from './scopeNumberCoercion';

const COUNT_FIELDS = {
  bedroom_suite: 'bedroom_count',
  balcony: 'balcony_count',
  help_room: 'help_room_count',
  store_room: 'store_room_count',
};

export const defaultValueForTemplateField = (field) => {
  const type = String(field?.type || '').toLowerCase();
  if (type === 'multi' || type === 'checkboxgroup') {
    return [];
  }
  if (type === 'checkbox' || type === 'boolean') {
    return false;
  }
  if (type === 'richtext' || type === 'richnotes' || type === 'notes') {
    return emptyTipTapNotes();
  }
  if (type === 'number') return '';
  return '';
};

/** Map template question → ScopeField-compatible field (with options resolved). */
export const resolveTemplateQuestion = (question, template) => {
  if (!question || typeof question !== 'object') return null;
  const optionSets = template?.optionSets || template?.raw?.option_sets || {};
  let options = question.options;
  if ((!options || !options.length) && question.options_ref) {
    options = optionSets[question.options_ref] || [];
  }
  if ((!options || !options.length) && question.id) {
    options =
      optionSets[question.id] ||
      optionSets[String(question.id).replace(/s$/, '')] ||
      optionSets[`${question.id}s`] ||
      [];
  }
  if (Array.isArray(options) && options.length) {
    options = options.map(normalizeOptionLabel);
  }

  const typeRaw = String(question.type || 'text').toLowerCase();
  let type = 'text';
  if (typeRaw === 'number' || typeRaw === 'integer' || typeRaw === 'float') {
    type = 'number';
  } else if (typeRaw === 'single' || typeRaw === 'singleselect') type = 'singleSelect';
  else if (typeRaw === 'multi' || typeRaw === 'checkboxgroup') {
    type = 'checkboxGroup';
  } else if (typeRaw === 'checkbox' || typeRaw === 'boolean') {
    type = 'checkbox';
  } else if (typeRaw === 'yes_no' || typeRaw === 'yesno') type = 'yesNo';
  else if (typeRaw === 'richtext' || typeRaw === 'richnotes' || typeRaw === 'notes') {
    type = 'richNotes';
  }

  // Dimension / qty ids must render as number inputs even if template types them as text.
  if (
    type === 'text' &&
    question.id &&
    isApiNumberFieldId(question.id) &&
    !isSingleSelectAnswerId(question.id)
  ) {
    type = 'number';
  }

  // Template contract: these ids are single-select even if typed loosely.
  if (isSingleSelectAnswerId(question.id)) {
    if (!options || !options.length) {
      if (String(question.id) === 'wardrobe_height') {
        options = ['Full Height', '8 ft', '8 ft + Loft'];
      } else if (
        String(question.id) === 'hardware_level' ||
        String(question.id).toLowerCase() === 'hardware'
      ) {
        options = [
          'Basic (Local Indian Brands)',
          'Mid (Hettich India / Ozone / Inox)',
          'Premium (Hafele / Hettich Germany)',
          'Ultra Premium (Blum Soft-Close)',
        ];
      }
    }
    if (options?.length) {
      type = 'singleSelect';
    }
  }

  const when = question.when || question.visibleWhen;
  let visibleWhen = null;
  if (when) {
    if (when.field) {
      visibleWhen = {
        field: when.field,
        ...(Object.prototype.hasOwnProperty.call(when, 'equals')
          ? { equals: when.equals }
          : {}),
        ...(Array.isArray(when.in) ? { in: when.in } : {}),
      };
    } else {
      visibleWhen = when;
    }
  }

  return {
    id: question.id,
    label: question.label || question.id,
    type,
    options:
      type === 'yesNo'
        ? question.options || ['Yes', 'No']
        : Array.isArray(options)
          ? options
          : [],
    options_ref: question.options_ref || null,
    required: Boolean(question.required),
    min: question.min,
    max: question.max,
    visibleWhen,
    minHeight: type === 'richNotes' ? 140 : undefined,
  };
};

/** Resolve choice options from the field or template option_sets (never invent). */
const resolveFieldOptionsFromSets = (field, optionSets = {}) => {
  if (Array.isArray(field?.options) && field.options.length > 0) {
    return field.options;
  }
  const id = String(field?.id || '');
  const keys = [
    field?.options_ref,
    id,
    id.replace(/s$/, ''),
    id.endsWith('s') ? id : `${id}s`,
    id.replace(/_types?$/, '_types'),
    id.replace(/_types?$/, '_type'),
  ].filter(Boolean);
  for (const key of keys) {
    const opts = optionSets[key];
    if (Array.isArray(opts) && opts.length > 0) return opts;
  }
  return [];
};

export const resolveGroup = (groupRef, template) => {
  const groups = template?.questionGroups || template?.raw?.question_groups || {};

  if (typeof groupRef === 'string') {
    const questions = groups[groupRef];
    return {
      id: groupRef,
      title: titleize(groupRef),
      questions: Array.isArray(questions) ? questions : [],
    };
  }

  if (groupRef && typeof groupRef === 'object') {
    const id = groupRef.id || groupRef.name || 'group';
    const questions = Array.isArray(groupRef.questions)
      ? groupRef.questions
      : Array.isArray(groups[id])
        ? groups[id]
        : [];
    return {
      id,
      title: groupRef.title || groupRef.label || titleize(id),
      questions,
    };
  }

  return { id: 'group', title: 'Group', questions: [] };
};

const titleize = (value = '') =>
  String(value)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

export const getRepeatCountField = (roomTemplate) =>
  roomTemplate?.repeat_count_field ||
  COUNT_FIELDS[roomTemplate?.room_type] ||
  null;

export const getProgramCount = (program, fieldId) => {
  const n = Number(program?.[fieldId]);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
};

/**
 * Ultimate Scope Sheet PDF section letters / titles (Build School by Eka).
 * Bedrooms use I, J, K… after H; help/store/balconies/notes keep fixed letters.
 */
export const PDF_SECTION_TITLES = {
  A: 'Project Information',
  B: 'Global Project Scope',
  C: 'Project Deliverables',
  D: 'Main Entrance',
  E: 'Foyer Area',
  F: 'Living / Drawing Room',
  G: 'Dining Area',
  H: 'Kitchen',
  L: 'Domestic Help Room',
  M: 'Store Room',
  N: 'Balconies',
  Q: 'Notes',
};

const ROOM_TYPE_PDF_LETTER = {
  main_entrance: 'D',
  foyer: 'E',
  foyer_area: 'E',
  living_drawing: 'F',
  living: 'F',
  drawing: 'F',
  dining: 'G',
  dining_area: 'G',
  kitchen: 'H',
  help_room: 'L',
  store_room: 'M',
  balcony: 'N',
};

const ROOM_TYPE_ORDER = [
  'main_entrance',
  'foyer',
  'foyer_area',
  'living_drawing',
  'living',
  'drawing',
  'dining',
  'dining_area',
  'kitchen',
  'bedroom_suite',
  'help_room',
  'store_room',
  'balcony',
];

const letterSortKey = (letter) => {
  const m = String(letter || '').match(/^([A-Z]+)(\d*)$/i);
  if (!m) return 999;
  const base = m[1].toUpperCase();
  const idx = base
    .split('')
    .reduce((acc, ch) => acc * 26 + (ch.charCodeAt(0) - 64), 0);
  return idx * 100 + (Number(m[2]) || 0);
};

export const getPdfLetterForRoomType = (roomType, index = 1) => {
  if (roomType === 'bedroom_suite') {
    return String.fromCharCode('I'.charCodeAt(0) + Math.max(0, index - 1));
  }
  return ROOM_TYPE_PDF_LETTER[roomType] || null;
};

export const getPdfTitleForLetter = (letter, fallback = '') => {
  const base = String(letter || '').replace(/\d+$/, '');
  return PDF_SECTION_TITLES[base] || fallback || `Section ${letter}`;
};

const resolveRoomTemplateLetter = (roomTemplate, index = 1) => {
  if (roomTemplate?.section) return String(roomTemplate.section).toUpperCase();
  return getPdfLetterForRoomType(roomTemplate?.room_type, index);
};

const bedroomTitle = (roomTemplate, index) =>
  roomTemplate?.title
    ? `${roomTemplate.title} ${index}`
    : `Bedroom ${index} & Washroom ${index}`;

/**
 * Wizard / nav steps aligned to PDF letters A–Q.
 * Each step: { id, letter, title, kind, sectionLetter?, programFields?, … }
 */
export const buildWizardSteps = (template, scopeDoc) => {
  const program = scopeDoc?.program || {};
  const rooms = Array.isArray(scopeDoc?.rooms) ? scopeDoc.rooms : [];
  const roomTemplates = [...(template?.roomTemplates || [])].sort((a, b) => {
    const ai = ROOM_TYPE_ORDER.indexOf(a?.room_type);
    const bi = ROOM_TYPE_ORDER.indexOf(b?.room_type);
    return (ai < 0 ? 99 : ai) - (bi < 0 ? 99 : bi);
  });

  const programByLetter = new Map();
  (template?.programFields || []).forEach((raw) => {
    const field = resolveTemplateQuestion(raw, template);
    if (!field) return;
    const letter = String(raw.section || 'A').toUpperCase();
    if (!programByLetter.has(letter)) programByLetter.set(letter, []);
    programByLetter.get(letter).push({ ...field, section: letter });
  });

  const roomLetters = new Set();
  roomTemplates.forEach((rt) => {
    if (rt?.room_type === 'bedroom_suite') return;
    const letter = resolveRoomTemplateLetter(rt, 1);
    if (letter) roomLetters.add(letter);
  });

  const steps = [];

  // A / B / C (and any other program-only letters not owned by a room section)
  [...programByLetter.keys()]
    .filter((letter) => !roomLetters.has(letter))
    .sort((a, b) => letterSortKey(a) - letterSortKey(b))
    .forEach((letter) => {
      steps.push({
        id: `program:${letter}`,
        letter,
        title: getPdfTitleForLetter(letter),
        kind: 'program',
        sectionLetter: letter,
        programFields: programByLetter.get(letter) || [],
      });
    });

  roomTemplates.forEach((rt) => {
    if (!rt?.room_type) return;

    if (rt.room_type === 'bedroom_suite' && rt.repeat) {
      const countField = getRepeatCountField(rt);
      const count = countField ? getProgramCount(program, countField) : 0;
      for (let i = 1; i <= count; i += 1) {
        const letter =
          resolveRoomTemplateLetter(rt, i) ||
          getPdfLetterForRoomType('bedroom_suite', i);
        const instance = rooms.find(
          (r) => r.room_type === 'bedroom_suite' && Number(r.index) === i
        );
        steps.push({
          id: `room:bedroom_suite:${i}`,
          letter,
          title: bedroomTitle(rt, i),
          kind: 'room',
          roomType: 'bedroom_suite',
          index: i,
          instanceId: instance?.instance_id || instance?.instanceId || null,
          roomTemplate: rt,
        });
      }
      return;
    }

    if (rt.room_type === 'balcony' && rt.repeat) {
      const letter = resolveRoomTemplateLetter(rt, 1) || 'N';
      const countField = getRepeatCountField(rt);
      const count = countField ? getProgramCount(program, countField) : 0;
      const programFields = programByLetter.get(letter) || [];
      if (count < 1 && !programFields.length) return;
      steps.push({
        id: `balconies:${letter}`,
        letter,
        title: getPdfTitleForLetter(letter, rt.title || 'Balconies'),
        kind: 'balcony_list',
        roomType: 'balcony',
        roomTemplate: rt,
        programFields,
        balconyIndexes: Array.from({ length: count }, (_, i) => i + 1),
      });
      return;
    }

    if (rt.repeat) {
      const letter = resolveRoomTemplateLetter(rt, 1);
      const countField = getRepeatCountField(rt);
      const count = countField ? getProgramCount(program, countField) : 0;
      const programFields = letter ? programByLetter.get(letter) || [] : [];
      if (count < 1 && !programFields.length) return;
      for (let i = 1; i <= Math.max(count, programFields.length ? 1 : 0); i += 1) {
        if (i > count && count > 0) break;
        if (count < 1) {
          steps.push({
            id: `program:${letter}`,
            letter,
            title: getPdfTitleForLetter(letter, rt.title),
            kind: 'program',
            sectionLetter: letter,
            programFields,
          });
          break;
        }
        const instance = rooms.find(
          (r) => r.room_type === rt.room_type && Number(r.index) === i
        );
        steps.push({
          id: `room:${rt.room_type}:${i}`,
          letter,
          title: rt.title || titleize(rt.room_type),
          kind: 'room',
          roomType: rt.room_type,
          index: i,
          instanceId: instance?.instance_id || instance?.instanceId || null,
          roomTemplate: rt,
          programFields: i === 1 ? programFields : [],
        });
      }
      return;
    }

    const letter = resolveRoomTemplateLetter(rt, 1);
    steps.push({
      id: `area:${rt.room_type}`,
      letter: letter || '?',
      title: getPdfTitleForLetter(letter, rt.title || titleize(rt.room_type)),
      kind: 'area',
      roomType: rt.room_type,
      roomTemplate: rt,
      programFields: letter ? programByLetter.get(letter) || [] : [],
    });
  });

  if (template?.documentNotes || template?.raw?.document_notes) {
    steps.push({
      id: 'document_notes',
      letter: 'Q',
      title: getPdfTitleForLetter(
        'Q',
        template.documentNotes?.label ||
          template.raw?.document_notes?.label ||
          'Notes'
      ),
      kind: 'document_notes',
    });
  }

  return steps.sort((a, b) => letterSortKey(a.letter) - letterSortKey(b.letter));
};

export const getResolvedFieldsForRoomTemplate = (roomTemplate, template) => {
  const groups = (roomTemplate?.groups || []).map((g) =>
    resolveGroup(g, template)
  );
  const roomType = roomTemplate?.room_type;
  const isLivingSectionF =
    roomType === 'living_drawing' ||
    roomType === 'living' ||
    roomType === 'drawing';
  const isDiningSectionG =
    roomType === 'dining' || roomType === 'dining_area';
  const isBedroomSectionI =
    roomType === 'bedroom_suite';
  const keepWallPanellingSizes =
    isLivingSectionF || isDiningSectionG || isBedroomSectionI;

  let resolved = groups.map((group) => {
    let fields = (group.questions || [])
      .map((q) => resolveTemplateQuestion(q, template))
      .filter(Boolean);
    // Sections F/G/I+ keep number of walls / wall sizes on wall panelling only
    // (never on "other carpentry")
    fields = sanitizeWallPanellingFields(group, fields, {
      keepSizes:
        keepWallPanellingSizes && !isOtherCarpentryGroup(group),
    });
    // Section D (Main Entrance): no wall panel materials / ceiling lights qty
    if (roomType === 'main_entrance') {
      fields = fields.filter(
        (f) => !isWallPanelMaterialField(f) && !isCeilingLightsQtyField(f)
      );
    }
    // Section F (Living / Drawing): no window type
    if (isLivingSectionF) {
      fields = fields.filter((f) => !isWindowTypeField(f));
    }
    // Section I+ (Bedrooms): no window type
    if (isBedroomSectionI) {
      fields = fields.filter((f) => !isWindowTypeField(f));
    }
    if (keepWallPanellingSizes) {
      fields = ensureLivingWallPanellingSizes(group, fields);
    }
    // Section H (Kitchen): no window type
    if (roomType === 'kitchen' || roomType === 'kitchen_area') {
      fields = fields.filter((f) => !isWindowTypeField(f));
    }
    return {
      ...group,
      fields: enrichFalseCeilingFields(group, fields, template),
    };
  });

  if (roomType === 'kitchen' || roomType === 'kitchen_area') {
    // Balcony Access must sit directly below window info (may be separate groups)
    resolved = ensureKitchenBalconyBelowWindows(resolved);
    // Carcass / Shutter / Hardware under "carpentry" (not construction type)
    resolved = ensureKitchenCarpentryGroup(resolved, template);
    // Construction type must stay multi-select (checkboxes)
    resolved = forceKitchenConstructionMultiSelect(resolved, template);
    // After Light Categories (Cove Lights) → Other Electrical
    resolved = splitKitchenOtherElectrical(resolved, template);
  }

  if (roomType === 'foyer' || roomType === 'foyer_area') {
    resolved = splitFoyerAdditionalElectrical(resolved, template);
  }

  if (
    roomType === 'living_drawing' ||
    roomType === 'living' ||
    roomType === 'drawing'
  ) {
    resolved = splitLivingOtherElectricals(resolved, template);
  }

  return resolved;
};

const isWallPanelMaterialField = (field) => {
  const id = String(field?.id || '').toLowerCase();
  const label = String(field?.label || '').toLowerCase();
  return (
    id.includes('wall_panel') ||
    id.includes('wallpanel') ||
    label.includes('wall panel')
  );
};

const isWallPanellingSizeOrCountField = (field) => {
  const id = String(field?.id || '').toLowerCase();
  const label = String(field?.label || '').toLowerCase();
  return (
    id.includes('number_of_walls') ||
    id.includes('numberofwalls') ||
    id.includes('wall_size') ||
    id.includes('wallsize') ||
    label.includes('number of walls') ||
    /^wall size\b/i.test(label)
  );
};

/** Drop wall-count/size fields (unless keepSizes); strip MDF from wall panel materials. */
const sanitizeWallPanellingFields = (
  group,
  fields = [],
  { keepSizes = false } = {}
) => {
  const groupId = String(group?.id || '').toLowerCase();
  const groupTitle = String(group?.title || '').toLowerCase();
  const inWallPanellingGroup =
    groupId.includes('wall_panel') ||
    groupId.includes('wallpanel') ||
    groupId.includes('carpentry') ||
    groupTitle.includes('wall panel') ||
    groupTitle.includes('carpentry') ||
    fields.some(
      (f) =>
        isWallPanelMaterialField(f) ||
        /wall.?panell?ing/i.test(f.id) ||
        /wall.?panell?ing/i.test(f.label || '')
    );

  return fields
    .filter((f) => keepSizes || !isWallPanellingSizeOrCountField(f))
    .map((field) => {
      if (!Array.isArray(field.options) || !field.options.length) return field;
      const id = String(field.id || '').toLowerCase();
      const label = String(field.label || '').toLowerCase();
      const isWallMaterials =
        isWallPanelMaterialField(field) ||
        id.includes('wall_material') ||
        id.includes('wallmaterial') ||
        ((id === 'materials' || label === 'materials') &&
          inWallPanellingGroup) ||
        (label.includes('material') && label.includes('wall'));
      if (!isWallMaterials) return field;
      const options = field.options.filter(
        (opt) => String(opt).toLowerCase() !== 'mdf'
      );
      if (options.length === field.options.length) return field;
      return { ...field, options };
    });
};

const isWindowTypeField = (field) => {
  const id = String(field?.id || '').toLowerCase();
  const label = String(field?.label || '').toLowerCase();
  return (
    id === 'window_type' ||
    id === 'windowtype' ||
    label === 'window type'
  );
};

const isBalconyAccessField = (field) => {
  const id = String(field?.id || '').toLowerCase();
  const label = String(field?.label || '').toLowerCase();
  return (
    id.includes('balcony_access') ||
    id.includes('balconyaccess') ||
    id.includes('balcony_door') ||
    id.includes('balconydoor') ||
    id.includes('balcony_railing') ||
    id.includes('balconyrailing') ||
    label.includes('balcony access') ||
    label.includes('balcony door') ||
    label.includes('balcony railing') ||
    label.includes('railing size') ||
    (label.includes('door size') && id.includes('balcony'))
  );
};

const isWindowInfoField = (field) => {
  const id = String(field?.id || '').toLowerCase();
  const label = String(field?.label || '').toLowerCase();
  return (
    id.includes('window') ||
    label.includes('window') ||
    id.includes('sill') ||
    label.includes('sill height') ||
    id.includes('lintel') ||
    label.includes('lintel')
  );
};

const defaultKitchenBalconyFields = () => [
  {
    id: 'balcony_access',
    label: 'Balcony Access',
    type: 'checkbox',
    options: [],
  },
  {
    id: 'balcony_door',
    label: 'Balcony Door',
    type: 'checkboxGroup',
    options: ['Sliding', 'Openable', 'Fixed'],
    visibleWhen: { field: 'balcony_access', equals: true },
  },
  {
    id: 'balcony_door_width',
    label: 'Door Size — Width',
    type: 'text',
    visibleWhen: { field: 'balcony_access', equals: true },
  },
  {
    id: 'balcony_door_height',
    label: 'Door Size — Height',
    type: 'text',
    visibleWhen: { field: 'balcony_access', equals: true },
  },
  {
    id: 'balcony_railing',
    label: 'Balcony Railing',
    type: 'checkboxGroup',
    options: ['Brick', 'Glass', 'Aluminium + Glass', 'SS', 'MS', 'Other'],
    visibleWhen: { field: 'balcony_access', equals: true },
  },
  {
    id: 'balcony_railing_size',
    label: 'Railing Size (r.ft / sq.ft)',
    type: 'text',
    visibleWhen: { field: 'balcony_access', equals: true },
  },
];

/** Merge API balcony fields with defaults so railing is always present. */
const mergeKitchenBalconyFields = (existing = []) => {
  const defaults = defaultKitchenBalconyFields();
  if (!existing.length) return defaults;

  const byKey = new Map();
  existing.forEach((f) => {
    const id = String(f.id || '').toLowerCase().replace(/[\s-]/g, '_');
    const label = String(f.label || '').toLowerCase();
    byKey.set(id, f);
    if (label.includes('balcony access')) byKey.set('balcony_access', f);
    if (label.includes('balcony door') && !label.includes('size')) {
      byKey.set('balcony_door', f);
    }
    if (label.includes('door size') && label.includes('width')) {
      byKey.set('balcony_door_width', f);
    }
    if (label.includes('door size') && label.includes('height')) {
      byKey.set('balcony_door_height', f);
    }
    if (label.includes('balcony railing') || label === 'railing') {
      byKey.set('balcony_railing', f);
    }
    if (label.includes('railing size')) {
      byKey.set('balcony_railing_size', f);
    }
  });

  const accessId =
    byKey.get('balcony_access')?.id ||
    byKey.get('balconyaccess')?.id ||
    'balcony_access';

  return defaults.map((def) => {
    const found =
      byKey.get(def.id) ||
      byKey.get(def.id.replace(/_/g, '')) ||
      null;
    if (!found) {
      return {
        ...def,
        visibleWhen: def.visibleWhen
          ? { ...def.visibleWhen, field: accessId }
          : undefined,
      };
    }
    return {
      ...def,
      ...found,
      label: found.label || def.label,
      type: found.type || def.type,
      options:
        Array.isArray(found.options) && found.options.length > 0
          ? found.options
          : def.options,
      visibleWhen: def.visibleWhen
        ? { field: accessId, equals: true }
        : found.visibleWhen,
    };
  });
};

/** Kitchen: Balcony Access as its own section, right after window info. */
const ensureKitchenBalconyBelowWindows = (groups = []) => {
  const balconyPool = [];
  let stripped = groups.map((group) => {
    const nextFields = [];
    (group.fields || []).forEach((f) => {
      if (isBalconyAccessField(f)) {
        balconyPool.push(f);
        return;
      }
      nextFields.push(f);
    });
    return { ...group, fields: nextFields };
  });

  // Drop empty leftover balcony-only groups
  stripped = stripped.filter((g) => {
    const id = String(g.id || '').toLowerCase();
    const title = String(g.title || '').toLowerCase();
    const isBalconyGroup =
      (id.includes('balcony') && !id.includes('door')) ||
      title.includes('balcony access');
    if (!isBalconyGroup) return true;
    return (g.fields || []).length > 0;
  });

  const balconyFields = mergeKitchenBalconyFields(balconyPool);

  const balconyGroup = {
    id: 'balcony_access',
    title: 'balcony access',
    fields: balconyFields,
  };

  // If window fields are mixed into basic info (no dedicated window group),
  // split them into a "window information" section first.
  const hasWindowGroup = stripped.some((g) => {
    const id = String(g.id || '').toLowerCase();
    const title = String(g.title || '').toLowerCase();
    return id.includes('window') || title.includes('window');
  });

  if (!hasWindowGroup) {
    const basicIdx = stripped.findIndex((g) => {
      const id = String(g.id || '').toLowerCase();
      const title = String(g.title || '').toLowerCase();
      return (
        id.includes('basic') ||
        title.includes('basic') ||
        (g.fields || []).some((f) => {
          const fid = String(f.id || '').toLowerCase();
          return fid === 'length' || fid === 'width';
        })
      );
    });

    if (basicIdx >= 0) {
      const basic = stripped[basicIdx];
      const windowFields = [];
      const otherFields = [];
      (basic.fields || []).forEach((f) => {
        if (isWindowInfoField(f)) windowFields.push(f);
        else otherFields.push(f);
      });

      if (windowFields.length > 0) {
        const notes = otherFields.filter((f) => f.type === 'richNotes');
        const withoutNotes = otherFields.filter((f) => f.type !== 'richNotes');
        stripped = [...stripped];
        stripped[basicIdx] = {
          ...basic,
          fields: [...withoutNotes, ...notes],
        };
        stripped.splice(basicIdx + 1, 0, {
          id: 'window_info',
          title: 'window information',
          fields: windowFields,
        });
      }
    }
  }

  // Find window-info group (by title/id or by window fields)
  let windowGroupIdx = stripped.findIndex((g) => {
    const id = String(g.id || '').toLowerCase();
    const title = String(g.title || '').toLowerCase();
    return id.includes('window') || title.includes('window');
  });

  if (windowGroupIdx < 0) {
    let lastWindowGroup = -1;
    stripped.forEach((group, gi) => {
      if ((group.fields || []).some(isWindowInfoField)) {
        lastWindowGroup = gi;
      }
    });
    windowGroupIdx = lastWindowGroup;
  }

  if (windowGroupIdx < 0) {
    windowGroupIdx = stripped.findIndex((g) => {
      const id = String(g.id || '').toLowerCase();
      const title = String(g.title || '').toLowerCase();
      return id.includes('basic') || title.includes('basic');
    });
  }

  if (windowGroupIdx < 0) {
    return [balconyGroup, ...stripped];
  }

  const next = [...stripped];
  next.splice(windowGroupIdx + 1, 0, balconyGroup);
  return next;
};

const isKitchenCarcassField = (field) => {
  const id = String(field?.id || '').toLowerCase().replace(/[\s-]/g, '_');
  const label = String(field?.label || '').toLowerCase();
  return (
    id.includes('carcass') ||
    label.includes('carcass material') ||
    label.trim() === 'carcass'
  );
};

const isKitchenShutterFinishField = (field) => {
  const id = String(field?.id || '').toLowerCase().replace(/[\s-]/g, '_');
  const label = String(field?.label || '').toLowerCase();
  if (label.includes('rolling') || id.includes('rolling')) return false;
  return (
    id.includes('shutter_finish') ||
    id.includes('shutterfinish') ||
    label.includes('shutter finish') ||
    (label.includes('shutter') && label.includes('finish')) ||
    (id.includes('shutter') && !id.includes('rolling'))
  );
};

const isKitchenHardwareLevelField = (field) => {
  const id = String(field?.id || '').toLowerCase().replace(/[\s-]/g, '_');
  const label = String(field?.label || '').toLowerCase();
  return (
    id.includes('hardware_level') ||
    id.includes('hardwarelevel') ||
    label.includes('hardware level') ||
    label.trim() === 'hardware' ||
    id === 'hardware' ||
    id.endsWith('_hardware')
  );
};

const normalizeKitchenChoiceField = (field, fallback) => {
  if (!field) return fallback;
  const options = (
    Array.isArray(field.options) && field.options.length > 0
      ? field.options
      : fallback.options
  ).map(normalizeOptionLabel);
  const preferSingle =
    fallback.type === 'singleSelect' ||
    fallback.type === 'yesNo' ||
    field.type === 'singleSelect' ||
    field.type === 'yesNo' ||
    isSingleSelectAnswerId(fallback.id) ||
    isSingleSelectAnswerId(field.id);
  return {
    ...fallback,
    ...field,
    label: field.label || fallback.label,
    type: preferSingle ? 'singleSelect' : fallback.type || 'checkboxGroup',
    options,
  };
};

const isKitchenFittingsOrCounterField = (field) => {
  const id = String(field?.id || '').toLowerCase().replace(/[\s-]/g, '_');
  const label = String(field?.label || '').toLowerCase();
  if (
    isKitchenCarcassField(field) ||
    isKitchenShutterFinishField(field) ||
    isKitchenHardwareLevelField(field)
  ) {
    return false;
  }
  return (
    id.includes('fitting') ||
    label.includes('fitting') ||
    id.includes('corner_unit') ||
    id.includes('cornerunit') ||
    id.includes('tandem') ||
    id.includes('bottle_pull') ||
    id.includes('bottlepull') ||
    id.includes('pantry') ||
    id.includes('rolling_shutter') ||
    id.includes('cutlery') ||
    id.includes('grain_trolley') ||
    id.includes('wicker') ||
    id.includes('dustbin') ||
    id.includes('glass_tray') ||
    id.includes('counter') ||
    label.includes('counter') ||
    id.includes('backsplash') ||
    label.includes('backsplash')
  );
};

const defaultKitchenFittingsAndCounterFields = (optionSets = {}) => [
  {
    id: 'fittings',
    label: 'Fittings',
    type: 'checkboxGroup',
    options:
      optionSets.kitchen_fittings ||
      optionSets.fittings || [
        'Corner Unit',
        'Tandem Drawers',
        'Bottle Pullout',
        'Pantry Unit',
        'Rolling Shutter',
        'Cutlery Tray',
        'Grain Trolley',
        'Wicker Basket',
        'Integrated Dustbin',
        'Glass Tray / Plate Tray',
      ],
  },
  {
    id: 'counter',
    label: 'Counter',
    type: 'checkboxGroup',
    options:
      optionSets.counter_material ||
      optionSets.counter || ['Granite', 'Quartz', 'Marble'],
  },
  {
    id: 'backsplash_material',
    label: 'Backsplash Material',
    type: 'text',
  },
];

/** FE-only qty fields removed from Kitchen H section — never show in UI. */
const isRemovedKitchenFittingQtyField = (field) => {
  const id = String(field?.id || '')
    .toLowerCase()
    .replace(/[\s-]/g, '_');
  const label = String(field?.label || '').toLowerCase();
  if (
    id === 'corner_unit_qty' ||
    id === 'tandem_drawers_qty' ||
    id === 'bottle_pullout_qty' ||
    id === 'cornerunitqty' ||
    id === 'tandemdrawersqty' ||
    id === 'bottlepulloutqty'
  ) {
    return true;
  }
  const isQty = id.includes('qty') || label.includes('qty');
  if (!isQty) return false;
  return (
    id.includes('corner_unit') ||
    id.includes('cornerunit') ||
    label.includes('corner unit') ||
    id.includes('tandem') ||
    label.includes('tandem') ||
    id.includes('bottle_pull') ||
    id.includes('bottlepull') ||
    label.includes('bottle pullout')
  );
};

const buildKitchenFittingsAndCounterBlock = (
  sourceFields = [],
  optionSets = {}
) => {
  const defaults = defaultKitchenFittingsAndCounterFields(optionSets);
  const filteredSource = (sourceFields || []).filter(
    (f) => !isRemovedKitchenFittingQtyField(f)
  );
  if (!filteredSource.length) return defaults;

  const used = new Set();
  const take = (pred) => {
    const found = filteredSource.find((f) => !used.has(f.id) && pred(f));
    if (found) used.add(found.id);
    return found;
  };

  const fittings =
    take(
      (f) =>
        String(f.id || '').toLowerCase().includes('fitting') ||
        String(f.label || '').toLowerCase().includes('fitting')
    ) || defaults[0];

  const counter =
    take(
      (f) =>
        String(f.id || '').toLowerCase().includes('counter') ||
        String(f.label || '').toLowerCase() === 'counter'
    ) || defaults[1];

  const backsplash =
    take(
      (f) =>
        String(f.id || '').toLowerCase().includes('backsplash') ||
        String(f.label || '').toLowerCase().includes('backsplash')
    ) || defaults[2];

  const leftovers = filteredSource.filter((f) => !used.has(f.id));

  return [
    normalizeKitchenChoiceField(fittings, defaults[0]),
    normalizeKitchenChoiceField(counter, defaults[1]),
    {
      ...defaults[2],
      ...backsplash,
      type: 'text',
      label: backsplash.label || defaults[2].label,
    },
    ...leftovers,
  ];
};

/**
 * Kitchen: Carcass Material, Shutter Finish, Hardware Level under "carpentry"
 * (never under "construction type"). Drop leftover "Hardware Level" headings.
 * Fittings + Counter sit directly below Hardware Level.
 */
const ensureKitchenCarpentryGroup = (groups = [], template = null) => {
  const optionSets = template?.optionSets || template?.raw?.option_sets || {};

  let carcass = null;
  let shutter = null;
  let hardware = null;
  const fittingsPool = [];
  const carpentryExtras = [];

  const isHardwareNamedGroup = (group) => {
    const id = String(group?.id || '').toLowerCase();
    const title = String(group?.title || '').toLowerCase();
    return (
      id.includes('hardware') ||
      title.includes('hardware level') ||
      title.trim() === 'hardware'
    );
  };

  let next = groups.map((group) => {
    const groupId = String(group?.id || '').toLowerCase();
    const groupTitle = String(group?.title || '').toLowerCase();
    const isConstruction =
      groupId.includes('construction') || groupTitle.includes('construction');
    const isCarpentryNamed =
      groupId.includes('carpentry') || groupTitle.includes('carpentry');
    const isHardwareNamed = isHardwareNamedGroup(group);
    const isFittingsNamed =
      groupId.includes('fitting') ||
      groupTitle.includes('fitting') ||
      groupId.includes('counter') ||
      groupTitle.includes('counter');

    const kept = [];
    (group.fields || []).forEach((f) => {
      if (isRemovedKitchenFittingQtyField(f)) return;
      if (isKitchenCarcassField(f)) {
        carcass = carcass || f;
        return;
      }
      if (isKitchenShutterFinishField(f)) {
        shutter = shutter || f;
        return;
      }
      if (isKitchenHardwareLevelField(f)) {
        hardware = hardware || f;
        return;
      }
      if (isKitchenFittingsOrCounterField(f) || isFittingsNamed) {
        fittingsPool.push(f);
        return;
      }
      // Fold carpentry + standalone hardware groups into carpentry
      if ((isCarpentryNamed || isHardwareNamed) && !isConstruction) {
        carpentryExtras.push(f);
        return;
      }
      kept.push(f);
    });
    return { ...group, fields: kept };
  });

  // Also pull fittings/counter left in empty-ish fittings groups
  next = next.map((group) => {
    const groupId = String(group?.id || '').toLowerCase();
    const groupTitle = String(group?.title || '').toLowerCase();
    if (
      groupId.includes('fitting') ||
      groupTitle.includes('fitting') ||
      groupId.includes('counter') ||
      groupTitle.includes('counter')
    ) {
      fittingsPool.push(...(group.fields || []));
      return { ...group, fields: [] };
    }
    return group;
  });

  carcass = normalizeKitchenChoiceField(carcass, {
    id: 'carcass_material',
    label: 'Carcass Material',
    type: 'checkboxGroup',
    options:
      optionSets.carcass_material || [
        'HDHMR',
        'BWP Below Sink Only',
        'BWP Entire Kitchen',
        'Plywood (BWR/BWP)',
      ],
  });
  shutter = normalizeKitchenChoiceField(shutter, {
    id: 'shutter_finish',
    label: 'Shutter Finish',
    type: 'checkboxGroup',
    options:
      optionSets.shutter_finish || [
        'Laminate',
        'Acrylic',
        'PU',
        'Veneer',
        'Glass',
      ],
  });
  hardware = normalizeKitchenChoiceField(hardware, {
    id: 'hardware_level',
    label: 'Hardware Level',
    type: 'singleSelect',
    options:
      optionSets.hardware_levels || [
        'Basic (Local Indian Brands)',
        'Mid (Hettich India / Ozone / Inox)',
        'Premium (Hafele / Hettich Germany)',
        'Ultra Premium (Blum Soft-Close)',
      ],
  });

  const fittingsBlock = buildKitchenFittingsAndCounterBlock(
    fittingsPool,
    optionSets
  );

  const extrasNotes = carpentryExtras.filter((f) => f.type === 'richNotes');
  const extrasRest = carpentryExtras.filter((f) => f.type !== 'richNotes');
  const carpentryFields = [
    carcass,
    shutter,
    hardware,
    ...fittingsBlock,
    ...extrasRest,
    ...extrasNotes,
  ];

  // Remove empty / leftover "Hardware Level" / fittings subsection headings
  next = next.filter((g) => {
    if (isHardwareNamedGroup(g)) return false;
    const fields = g.fields || [];
    const id = String(g.id || '').toLowerCase();
    const title = String(g.title || '').toLowerCase();
    if (
      id.includes('fitting') ||
      title.includes('fitting') ||
      ((id.includes('counter') || title.includes('counter')) &&
        fields.length === 0)
    ) {
      return false;
    }
    if (fields.length === 0) {
      if (
        id.includes('hardware') ||
        title.includes('hardware') ||
        id.includes('carcass') ||
        title.includes('carcass') ||
        id.includes('shutter') ||
        title.includes('shutter')
      ) {
        return false;
      }
    }
    return fields.length > 0 || String(g.id || '').includes('carpentry');
  });

  const carpentryIdx = next.findIndex((g) => {
    const id = String(g.id || '').toLowerCase();
    const title = String(g.title || '').toLowerCase();
    return id.includes('carpentry') || title.includes('carpentry');
  });

  const constructionIdx = next.findIndex((g) => {
    const id = String(g.id || '').toLowerCase();
    const title = String(g.title || '').toLowerCase();
    return id.includes('construction') || title.includes('construction');
  });

  if (carpentryIdx >= 0) {
    next = next.map((g, i) =>
      i === carpentryIdx
        ? {
            ...g,
            id: g.id || 'carpentry',
            title: 'carpentry',
            fields: carpentryFields,
          }
        : g
    );
  } else {
    const carpentryGroup = {
      id: 'carpentry',
      title: 'carpentry',
      fields: carpentryFields,
    };
    if (constructionIdx >= 0) {
      next = [...next];
      next.splice(constructionIdx + 1, 0, carpentryGroup);
    } else {
      const electricalIdx = next.findIndex((g) => {
        const id = String(g.id || '').toLowerCase();
        const title = String(g.title || '').toLowerCase();
        return id.includes('electrical') || title.includes('electrical');
      });
      if (electricalIdx >= 0) {
        next = [...next];
        next.splice(electricalIdx, 0, carpentryGroup);
      } else {
        next = [...next, carpentryGroup];
      }
    }
  }

  next = next.map((g) => {
    const id = String(g.id || '').toLowerCase();
    const title = String(g.title || '').toLowerCase();
    if (id.includes('construction') || title.includes('construction')) {
      return { ...g, title: 'construction type' };
    }
    if (id.includes('carpentry') || title.includes('carpentry')) {
      return { ...g, title: 'carpentry' };
    }
    return g;
  });

  return next.filter((g) => !isHardwareNamedGroup(g));
};

const DEFAULT_KITCHEN_CONSTRUCTION_OPTIONS = [
  'On-Site Carpenter',
  'Carcass On-Site + Factory-Made Shutters',
  'Full Modular Factory-Made Kitchen',
];

const isKitchenConstructionTypeField = (field) => {
  const id = String(field?.id || '')
    .toLowerCase()
    .replace(/[\s-]/g, '_');
  const label = String(field?.label || '').toLowerCase();
  if (
    isKitchenCarcassField(field) ||
    isKitchenShutterFinishField(field) ||
    isKitchenHardwareLevelField(field) ||
    isKitchenFittingsOrCounterField(field)
  ) {
    return false;
  }
  return (
    id === 'types' ||
    id === 'type' ||
    id === 'construction' ||
    id.includes('construction_type') ||
    id.includes('constructiontype') ||
    id.includes('kitchen_construction') ||
    label.includes('construction type') ||
    (label.includes('construction') && !label.includes('false'))
  );
};

/**
 * Kitchen Construction Type is multi-select in product UX even if the
 * Stage template ships it as single-select.
 */
const forceKitchenConstructionMultiSelect = (groups = [], template = null) => {
  const optionSets = template?.optionSets || template?.raw?.option_sets || {};
  const fallbackOptions =
    optionSets.kitchen_construction ||
    optionSets.construction_type ||
    optionSets.construction_types ||
    optionSets.types ||
    DEFAULT_KITCHEN_CONSTRUCTION_OPTIONS;

  return (groups || []).map((group) => {
    const groupId = String(group?.id || '').toLowerCase();
    const groupTitle = String(group?.title || '').toLowerCase();
    const isConstructionGroup =
      groupId.includes('construction') || groupTitle.includes('construction');

    const fields = (group.fields || []).map((field) => {
      if (field?.type === 'richNotes' || field?.type === 'notes') {
        return field;
      }

      const matchesField = isKitchenConstructionTypeField(field);
      const choiceInConstructionGroup =
        isConstructionGroup &&
        (field?.type === 'singleSelect' ||
          field?.type === 'yesNo' ||
          field?.type === 'checkboxGroup') &&
        Array.isArray(field?.options) &&
        field.options.length > 0 &&
        !isKitchenCarcassField(field) &&
        !isKitchenShutterFinishField(field) &&
        !isKitchenHardwareLevelField(field) &&
        !isKitchenFittingsOrCounterField(field);

      if (!matchesField && !choiceInConstructionGroup) {
        return field;
      }

      const options = (
        Array.isArray(field.options) && field.options.length > 0
          ? field.options
          : fallbackOptions
      ).map(normalizeOptionLabel);

      return {
        ...field,
        type: 'checkboxGroup',
        label: field.label || 'Construction type',
        options,
      };
    });

    return { ...group, fields };
  });
};

const isOtherCarpentryGroup = (group) => {
  const id = String(group?.id || '').toLowerCase();
  const title = String(group?.title || '').toLowerCase();
  return (
    id.includes('other_carpentry') ||
    id.includes('othercarpentry') ||
    title.includes('other carpentry') ||
    (title.includes('other') && title.includes('carpentry'))
  );
};

/** Ensure Number of Walls + Wall Size fields exist for wall panelling (F/G/I+).
 *  Never keep them under "other carpentry".
 */
const ensureLivingWallPanellingSizes = (group, fields = []) => {
  if (isOtherCarpentryGroup(group)) {
    return fields.filter((f) => !isWallPanellingSizeOrCountField(f));
  }

  const groupId = String(group?.id || '').toLowerCase();
  const groupTitle = String(group?.title || '').toLowerCase();
  const hasWallPanellingSignal = fields.some(
    (f) =>
      /wall.?panell?ing/i.test(f.id) ||
      /wall.?panell?ing/i.test(f.label || '') ||
      isWallPanelMaterialField(f)
  );
  const isWallGroup =
    groupId.includes('wall_panel') ||
    groupTitle.includes('wall panel') ||
    groupTitle.includes('wall panelling') ||
    hasWallPanellingSignal ||
    ((groupId.includes('carpentry') || groupTitle.includes('carpentry')) &&
      hasWallPanellingSignal);
  if (!isWallGroup) return fields;

  const gate =
    fields.find(
      (f) =>
        /wall.?panell?ing.?required/i.test(f.id) ||
        /wall.?panell?ing.?required/i.test(f.label || '') ||
        (String(f.id || '').toLowerCase() === 'required' &&
          /panel/i.test(groupTitle))
    ) || null;
  const gateId = gate?.id || 'wall_panelling_required';

  let next = [...fields];
  const hasCount = next.some((f) => {
    const id = String(f.id || '').toLowerCase();
    const label = String(f.label || '').toLowerCase();
    return (
      id.includes('number_of_walls') ||
      id.includes('numberofwalls') ||
      label.includes('number of walls')
    );
  });
  const hasSize1 = next.some((f) => {
    const id = String(f.id || '').toLowerCase();
    return (
      id.includes('wall_size') ||
      id.includes('wallsize') ||
      id === 'wall_size_1'
    );
  });

  if (!hasCount) {
    const insertAt = gate
      ? next.findIndex((f) => f.id === gate.id) + 1
      : 0;
    next.splice(insertAt, 0, {
      id: 'number_of_walls',
      label: 'Number of Walls',
      type: 'singleSelect',
      options: ['1', '2', '3'],
      visibleWhen: { field: gateId, equals: true },
    });
  }

  if (!hasSize1) {
    const countField = next.find((f) => {
      const id = String(f.id || '').toLowerCase();
      return (
        id.includes('number_of_walls') || id.includes('numberofwalls')
      );
    });
    const countId = countField?.id || 'number_of_walls';
    const materialsIdx = next.findIndex(
      (f) =>
        isWallPanelMaterialField(f) ||
        String(f.id || '').toLowerCase().includes('material')
    );
    const insertAt = materialsIdx >= 0 ? materialsIdx + 1 : next.length;
    next.splice(
      insertAt,
      0,
      {
        id: 'wall_size_1',
        label: 'Wall Size 1',
        type: 'text',
        visibleWhen: [
          { field: gateId, equals: true },
          { field: countId, in: ['1', '2', '3'] },
        ],
      },
      {
        id: 'wall_size_2',
        label: 'Wall Size 2',
        type: 'text',
        visibleWhen: [
          { field: gateId, equals: true },
          { field: countId, in: ['2', '3'] },
        ],
      },
      {
        id: 'wall_size_3',
        label: 'Wall Size 3',
        type: 'text',
        visibleWhen: [
          { field: gateId, equals: true },
          { field: countId, in: ['3'] },
        ],
      }
    );
  }

  return next.map((f) => {
    if (!f.visibleWhen) return f;
    if (gate?.type === 'checkbox' || gate?.type === 'boolean') {
      return {
        ...f,
        visibleWhen: Array.isArray(f.visibleWhen)
          ? f.visibleWhen.map((r) =>
              r?.field === gateId ? { ...r, equals: true } : r
            )
          : { ...f.visibleWhen, equals: true },
      };
    }
    if (gate?.type === 'yesNo') {
      return {
        ...f,
        visibleWhen: Array.isArray(f.visibleWhen)
          ? f.visibleWhen.map((r) =>
              r?.field === gateId ? { ...r, equals: 'Yes' } : r
            )
          : { ...f.visibleWhen, equals: 'Yes' },
      };
    }
    return f;
  });
};

const isCeilingLightsQtyField = (field) => {
  const id = String(field?.id || '').toLowerCase();
  const label = String(field?.label || '').toLowerCase();
  return (
    id.includes('ceiling_lights_qty') ||
    id.includes('ceilinglightsqty') ||
    id === 'ceiling_lights_nos' ||
    (label.includes('ceiling light') &&
      (label.includes('qty') ||
        label.includes('quantity') ||
        label.includes('(nos)') ||
        label.includes('nos')))
  );
};

const isLightCategoriesField = (field) => {
  const id = String(field?.id || '').toLowerCase();
  const label = String(field?.label || '').toLowerCase();
  return (
    id === 'light_categories' ||
    id === 'lightcategories' ||
    label.includes('light categor')
  );
};

const KITCHEN_LIGHT_CATEGORY_OPTIONS = [
  'COB',
  'Panel Lights',
  'Profile Lights',
  'Cove Lights',
];

const trimKitchenLightCategories = (field) => {
  if (!isLightCategoriesField(field)) return field;
  const options =
    Array.isArray(field.options) && field.options.length > 0
      ? field.options.filter((opt) =>
          KITCHEN_LIGHT_CATEGORY_OPTIONS.some(
            (keep) => String(opt).toLowerCase() === keep.toLowerCase()
          )
        )
      : KITCHEN_LIGHT_CATEGORY_OPTIONS;
  return {
    ...field,
    options: options.length > 0 ? options : KITCHEN_LIGHT_CATEGORY_OPTIONS,
  };
};

const isKitchenOtherElectricalField = (field) => {
  const id = String(field?.id || '').toLowerCase().replace(/[\s-]/g, '_');
  const label = String(field?.label || '').toLowerCase();
  if (isLightCategoriesField(field)) return false;
  if (field?.type === 'richNotes') return false;
  if (
    id.includes('wiring_brand') ||
    id.includes('switch') ||
    label.includes('wiring brand') ||
    label.includes('switch')
  ) {
    return false;
  }
  return (
    id.includes('fans') ||
    label.includes('fans') ||
    id.includes('ac_wiring') ||
    id.includes('acwiring') ||
    label.includes('ac wiring') ||
    id.includes('speaker') ||
    label.includes('speaker') ||
    id.includes('ac_type') ||
    id.includes('actype') ||
    label.includes('air condition') ||
    id.includes('approx_wiring') ||
    id.includes('wiring_length') ||
    label.includes('wiring length') ||
    id.includes('heavy') ||
    label.includes('heavy appliance') ||
    id.includes('wire_type') ||
    id.includes('wire_safety') ||
    label.includes('wire safety') ||
    label.includes('wire type') ||
    id === 'lights' ||
    label === 'lights'
  );
};

const buildKitchenOtherElectricalFields = (sourceFields = [], template) => {
  const optionSets = template?.optionSets || template?.raw?.option_sets || {};

  const fans = ensureCheckboxField(
    findFieldByAliases(
      sourceFields,
      ['fans'],
      { excludeIds: ['qty', 'quantity'] }
    ),
    'fans',
    'Fans'
  );

  const fansQty =
    findFieldByAliases(sourceFields, ['fans_qty', 'fansqty']) || {
      id: 'fans_qty',
      label: 'Fans (Qty)',
      type: 'number',
    };

  const acWiring = ensureCheckboxField(
    findFieldByAliases(
      sourceFields,
      ['ac_wiring', 'acwiring'],
      { excludeIds: ['qty', 'quantity'] }
    ),
    'ac_wiring',
    'AC Wiring'
  );

  const acWiringQty =
    findFieldByAliases(sourceFields, [
      'ac_wiring_qty',
      'acwiringqty',
    ]) || {
      id: 'ac_wiring_qty',
      label: 'AC Wiring (Qty)',
      type: 'number',
    };

  const speakers = ensureCheckboxField(
    findFieldByAliases(sourceFields, [
      'speakers_required',
      'speakersrequired',
      'speakers',
    ]),
    'speakers_required',
    'Speakers Required'
  );

  const acTypes = findFieldByAliases(
    sourceFields,
    [
      'ac_type',
      'ac_types',
      'actype',
      'actypes',
      'air_condition_types',
      'airconditiontypes',
      'air_condition_type',
      'air condition types',
      'air condition type',
    ],
    { excludeIds: ['wiring', 'qty', 'quantity', 'length'], fuzzy: true }
  );

  const wiringLength =
    findFieldByAliases(sourceFields, [
      'approx_wiring_length',
      'wiring_length',
    ]) || {
      id: 'approx_wiring_length',
      label: 'Approx. Wiring Length',
      type: 'text',
    };

  const heavy =
    findFieldByAliases(sourceFields, [
      'heavy_appliances',
      'heavyappliances',
    ]) || {
      id: 'heavy_appliances',
      label: 'Heavy Appliances',
      type: 'checkboxGroup',
      options:
        optionSets.heavy_appliances || [
          'Hob Point',
          'Chimney + Duct Route',
          'Microwave',
          'Oven',
          'Refrigerator',
          'Dishwasher Electrical Point',
          'Exhaust',
          'Aquaguard / RO',
        ],
    };

  const wireSafety =
    findFieldByAliases(sourceFields, [
      'wire_type',
      'wiretype',
      'wire_safety',
      'wiresafety',
    ]) || {
      id: 'wire_type',
      label: 'Wire Safety',
      type: 'checkboxGroup',
      options:
        optionSets.wire_types ||
        optionSets.wire_safety || ['FR', 'FRLS', 'Non-FR'],
    };

  const lights = findFieldByAliases(sourceFields, ['lights']) || {
    id: 'lights',
    label: 'Lights',
    type: 'checkboxGroup',
    options:
      optionSets.kitchen_lights || [
        'Under-Cabinet LED',
        'Ceiling Lights',
        'Profile Lights',
      ],
  };

  const lightsField =
    String(lights.label || '').toLowerCase().includes('categor') ||
    String(lights.id || '').toLowerCase().includes('categor')
      ? {
          id: 'lights',
          label: 'Lights',
          type: 'checkboxGroup',
          options: [
            'Under-Cabinet LED',
            'Ceiling Lights',
            'Profile Lights',
          ],
        }
      : lights;

  const notesField = sourceFields.find((f) => f.type === 'richNotes');

  return [
    fans,
    {
      ...fansQty,
      id: fansQty.id || 'fans_qty',
      label: 'Fans (Qty)',
      type: 'number',
      visibleWhen: { field: fans.id, equals: true },
    },
    acWiring,
    {
      ...acWiringQty,
      id: acWiringQty.id || 'ac_wiring_qty',
      label: 'AC Wiring (Qty)',
      type: 'number',
      visibleWhen: { field: acWiring.id, equals: true },
    },
    speakers,
    ...(acTypes
      ? [
          {
            ...acTypes,
            // Keep template type when present (single vs multi)
            type:
              acTypes.type === 'singleSelect' || acTypes.type === 'yesNo'
                ? acTypes.type
                : 'checkboxGroup',
            label: acTypes.label || 'Air Condition Types',
            options: resolveFieldOptionsFromSets(acTypes, optionSets),
          },
        ]
      : []),
    {
      ...wiringLength,
      type: 'text',
      label: wiringLength.label || 'Approx. Wiring Length',
    },
    {
      ...heavy,
      type: 'checkboxGroup',
      label: heavy.label || 'Heavy Appliances',
      options:
        heavy.options?.length > 0
          ? heavy.options
          : [
              'Hob Point',
              'Chimney + Duct Route',
              'Microwave',
              'Oven',
              'Refrigerator',
              'Dishwasher Electrical Point',
              'Exhaust',
              'Aquaguard / RO',
            ],
    },
    {
      ...wireSafety,
      type: 'checkboxGroup',
      label: 'Wire Safety',
      options:
        wireSafety.options?.length > 0
          ? wireSafety.options
          : ['FR', 'FRLS', 'Non-FR'],
    },
    {
      ...lightsField,
      type: 'checkboxGroup',
      label: 'Lights',
      options:
        lightsField.options?.length > 0
          ? lightsField.options.filter(
              (o) => !String(o).toLowerCase().includes('cove')
            )
          : ['Under-Cabinet LED', 'Ceiling Lights', 'Profile Lights'],
    },
    ...(notesField ? [notesField] : []),
  ];
};

/**
 * Kitchen: after Light Categories (Cove Lights), show "Other Electrical"
 * with Fans, AC Wiring, Speakers Required first.
 */
const splitKitchenOtherElectrical = (groups, template) => {
  const out = [];
  const pooledFields = [];
  let electricalCorePushed = false;
  let otherInserted = false;

  const insertOther = () => {
    if (otherInserted) return;
    out.push({
      id: 'other_electrical',
      title: 'other electrical',
      fields: buildKitchenOtherElectricalFields(pooledFields, template),
    });
    otherInserted = true;
  };

  groups.forEach((group) => {
    const id = String(group?.id || '').toLowerCase();
    const title = String(group?.title || '').toLowerCase();

    if (
      id.includes('other_electrical') ||
      title.includes('other electrical')
    ) {
      pooledFields.push(...(group.fields || []));
      return;
    }

    if (
      id.includes('paint') ||
      title.includes('paint') ||
      id.includes('plumb') ||
      title.includes('plumb')
    ) {
      if (electricalCorePushed) insertOther();
      out.push(group);
      return;
    }

    if (!isElectricalGroup(group)) {
      out.push(group);
      return;
    }

    const fields = (group.fields || []).map(trimKitchenLightCategories);
    pooledFields.push(...fields);

    if (electricalCorePushed) return;

    const lightIdx = fields.findIndex(isLightCategoriesField);

    const coreFields = (
      lightIdx >= 0
        ? fields.slice(0, lightIdx + 1)
        : fields.filter((f) => !isKitchenOtherElectricalField(f))
    )
      .filter(
        (f) => !isKitchenOtherElectricalField(f) || isLightCategoriesField(f)
      )
      .map(trimKitchenLightCategories);

    out.push({
      ...group,
      id: group.id || 'electrical',
      title: 'electrical',
      fields: coreFields,
    });
    electricalCorePushed = true;
  });

  insertOther();

  const paint = [];
  const plumbing = [];
  const rest = [];
  out.forEach((g) => {
    const gid = String(g?.id || '').toLowerCase();
    const gtitle = String(g?.title || '').toLowerCase();
    if (gid.includes('paint') || gtitle.includes('paint')) paint.push(g);
    else if (gid.includes('plumb') || gtitle.includes('plumb')) plumbing.push(g);
    else rest.push(g);
  });

  const otherIdx = rest.findIndex(
    (g) =>
      String(g?.id || '')
        .toLowerCase()
        .includes('other_electrical') ||
      String(g?.title || '')
        .toLowerCase()
        .includes('other electrical')
  );

  if (otherIdx >= 0) {
    return [
      ...rest.slice(0, otherIdx + 1),
      ...paint,
      ...plumbing,
      ...rest.slice(otherIdx + 1),
    ];
  }
  return [...rest, ...paint, ...plumbing];
};

const isElectricalGroup = (group) => {
  const id = String(group?.id || '').toLowerCase();
  const title = String(group?.title || '').toLowerCase();
  return id.includes('electrical') || title.includes('electrical');
};

const isFoyerAdditionalOnlyGroup = (group) => {
  const id = String(group?.id || '').toLowerCase();
  const title = String(group?.title || '').toLowerCase();
  // Never fold soft furnishing / paint into Additional Electrical
  if (isSoftFurnishingOrPaintGroup(group)) return false;
  if (id.includes('additional') || title.includes('additional')) return true;
  if (id.includes('entrance_bell') || title.includes('entrance bell')) return true;
  if (
    id.includes('air_condition') ||
    id.includes('ac_type') ||
    title.includes('air condition')
  ) {
    return true;
  }
  if (
    id.includes('approx_wiring') ||
    (title.includes('approx') && title.includes('wiring'))
  ) {
    return true;
  }
  const fields = group?.fields || [];
  if (!fields.length) return false;
  return fields.every((f) => isFoyerAdditionalFieldId(f.id, f.label));
};

const isSoftFurnishingOrPaintGroup = (group) => {
  const id = String(group?.id || '').toLowerCase();
  const title = String(group?.title || '').toLowerCase();
  return (
    id.includes('soft_furnish') ||
    id.includes('softfurnish') ||
    title.includes('soft furnish') ||
    id === 'paint' ||
    id.includes('paint') ||
    title === 'paint' ||
    title.includes('paint')
  );
};

const isFoyerAdditionalFieldId = (id, label) => {
  const key = `${id || ''} ${label || ''}`.toLowerCase();
  return (
    key.includes('wall_light') ||
    key.includes('wall light') ||
    key.includes('console_led') ||
    key.includes('console led') ||
    key.includes('ac_wiring') ||
    key.includes('ac wiring') ||
    key.includes('speaker') ||
    key.includes('automation') ||
    key.includes('entrance_bell') ||
    key.includes('entrance bell') ||
    key.includes('ac_type') ||
    key.includes('air_condition') ||
    key.includes('air condition') ||
    key.includes('approx_wiring') ||
    key.includes('approx. wiring') ||
    key.includes('wiring length')
  );
};

/**
 * Section F: after Light Categories (Cove Lights), show "Other Electricals"
 * before paint.
 */
const splitLivingOtherElectricals = (groups, template) => {
  const out = [];
  const pooledFields = [];
  let electricalCorePushed = false;
  let otherInserted = false;

  const insertOther = () => {
    if (otherInserted) return;
    out.push({
      id: 'other_electricals',
      title: 'other electricals',
      fields: buildLivingOtherElectricalFields(pooledFields, template),
    });
    otherInserted = true;
  };

  groups.forEach((group) => {
    const id = String(group?.id || '').toLowerCase();
    const title = String(group?.title || '').toLowerCase();

    if (id.includes('other_electrical') || title.includes('other electrical')) {
      pooledFields.push(...(group.fields || []));
      return;
    }

    if (isSoftFurnishingOrPaintGroup(group)) {
      if (electricalCorePushed) insertOther();
      out.push(group);
      return;
    }

    if (!isElectricalGroup(group)) {
      out.push(group);
      return;
    }

    const fields = group.fields || [];
    pooledFields.push(...fields);

    if (
      id.includes('additional') ||
      title.includes('additional') ||
      title.includes('other electrical')
    ) {
      return;
    }

    if (electricalCorePushed) return;

    const lightIdx = fields.findIndex(isLightCategoriesField);
    const otherIds = new Set(
      [
        'wall_lights',
        'walllight',
        'wall_lights_qty',
        'fans',
        'fans_qty',
        'ac_wiring',
        'acwiring',
        'speakers',
        'automation',
        'chandelier',
        'ac_types',
        'air_condition',
        'approx_wiring',
        'wiring_length',
      ].map((s) => s.toLowerCase())
    );
    const isOtherField = (f) => {
      const fid = String(f.id || '').toLowerCase();
      const flabel = String(f.label || '').toLowerCase();
      if (isLightCategoriesField(f)) return false;
      if (f.type === 'richNotes') return false;
      if (
        fid.includes('wiring_brand') ||
        fid.includes('wire_type') ||
        fid.includes('switch')
      ) {
        return false;
      }
      return (
        [...otherIds].some((k) => fid.includes(k) || flabel.includes(k.replace(/_/g, ' '))) ||
        flabel.includes('wall light') ||
        flabel.includes('fan') ||
        flabel.includes('speaker') ||
        flabel.includes('automation') ||
        flabel.includes('chandelier') ||
        flabel.includes('air condition') ||
        flabel.includes('approx') ||
        flabel.includes('ac wiring')
      );
    };

    const coreFields = (
      lightIdx >= 0 ? fields.slice(0, lightIdx + 1) : fields.filter((f) => !isOtherField(f))
    ).filter((f) => !isOtherField(f) || isLightCategoriesField(f));

    const notesField = fields.find((f) => f.type === 'richNotes');
    if (notesField && !coreFields.some((f) => f.id === notesField.id)) {
      coreFields.push(notesField);
    }

    out.push({
      ...group,
      fields: coreFields,
    });
    electricalCorePushed = true;
  });

  insertOther();

  // Order: … electrical, other electricals, soft?, paint
  const soft = [];
  const paint = [];
  const rest = [];
  out.forEach((g) => {
    const id = String(g?.id || '').toLowerCase();
    const title = String(g?.title || '').toLowerCase();
    if (id.includes('soft') || title.includes('soft furnish')) soft.push(g);
    else if (id.includes('paint') || title.includes('paint')) paint.push(g);
    else rest.push(g);
  });
  const otherIdx = rest.findIndex(
    (g) =>
      String(g?.id || '')
        .toLowerCase()
        .includes('other_electrical') ||
      String(g?.title || '')
        .toLowerCase()
        .includes('other electrical')
  );
  if (otherIdx >= 0) {
    return [
      ...rest.slice(0, otherIdx + 1),
      ...soft,
      ...paint,
      ...rest.slice(otherIdx + 1),
    ];
  }
  return [...rest, ...soft, ...paint];
};

const buildLivingOtherElectricalFields = (sourceFields = [], template) => {
  const optionSets = template?.optionSets || template?.raw?.option_sets || {};

  const wallLights = ensureCheckboxField(
    findFieldByAliases(
      sourceFields,
      ['wall_lights', 'walllights'],
      { excludeIds: ['qty', 'quantity', 'nos'] }
    ),
    'wall_lights',
    'Wall Lights'
  );

  const wallLightsQty =
    findFieldByAliases(sourceFields, [
      'wall_lights_qty',
      'walllightsqty',
    ]) || {
      id: 'wall_lights_qty',
      label: 'Wall Lights (Qty)',
      type: 'number',
    };

  const fans = ensureCheckboxField(
    findFieldByAliases(
      sourceFields,
      ['fans'],
      { excludeIds: ['qty', 'quantity'] }
    ),
    'fans',
    'Fans'
  );

  const fansQty =
    findFieldByAliases(sourceFields, ['fans_qty', 'fansqty']) || {
      id: 'fans_qty',
      label: 'Fans (Qty)',
      type: 'number',
    };

  const acWiring = ensureCheckboxField(
    findFieldByAliases(
      sourceFields,
      ['ac_wiring', 'acwiring'],
      { excludeIds: ['qty', 'quantity'] }
    ),
    'ac_wiring',
    'AC Wiring'
  );

  const acWiringQty =
    findFieldByAliases(sourceFields, [
      'ac_wiring_qty',
      'acwiringqty',
    ]) || {
      id: 'ac_wiring_qty',
      label: 'AC Wiring (Qty)',
      type: 'number',
    };

  const chandelier = ensureCheckboxField(
    findFieldByAliases(sourceFields, ['chandelier']),
    'chandelier',
    'Chandelier'
  );

  const speakers = ensureCheckboxField(
    findFieldByAliases(sourceFields, [
      'speakers_required',
      'speakersrequired',
      'speakers',
    ]),
    'speakers_required',
    'Speakers Required'
  );

  const automation = ensureCheckboxField(
    findFieldByAliases(sourceFields, [
      'automation_required',
      'automationrequired',
    ]),
    'automation_required',
    'Automation Required'
  );

  const automationItems =
    findFieldByAliases(sourceFields, [
      'automation_items',
      'automationitems',
    ]) || {
      id: 'automation_items',
      label: 'Automation Items',
      type: 'checkboxGroup',
      options:
        optionSets.automation_items || [
          'Lights',
          'Curtains',
          'AC',
          'TV',
          'Speakers',
          'Scene Settings',
        ],
    };

  const acTypes =
    findFieldByAliases(
      sourceFields,
      ['ac_types', 'actypes', 'air_condition_types'],
      { excludeIds: ['wiring', 'qty'] }
    ) || {
      id: 'ac_types',
      label: 'Air Condition Types',
      type: 'checkboxGroup',
      options:
        optionSets.ac_types ||
        optionSets.air_condition_types || ['Split', 'Cassette', 'Ductable'],
    };

  const approxWiring =
    findFieldByAliases(sourceFields, [
      'approx_wiring_length',
      'approxwiringlength',
      'wiring_length',
    ]) || {
      id: 'approx_wiring_length',
      label: 'Approx. Wiring Length',
      type: 'text',
    };

  const fields = [
    wallLights,
    {
      ...wallLightsQty,
      type: 'number',
      label: wallLightsQty.label || 'Wall Lights (Qty)',
      visibleWhen: { field: wallLights.id, equals: true },
    },
    fans,
    {
      ...fansQty,
      type: 'number',
      label: fansQty.label || 'Fans (Qty)',
      visibleWhen: { field: fans.id, equals: true },
    },
    acWiring,
    {
      ...acWiringQty,
      type: 'number',
      label: acWiringQty.label || 'AC Wiring (Qty)',
      visibleWhen: { field: acWiring.id, equals: true },
    },
    chandelier,
    speakers,
    automation,
    {
      ...automationItems,
      type: 'checkboxGroup',
      visibleWhen: { field: automation.id, equals: true },
      options: (() => {
        const opts =
          automationItems.options?.length > 0
            ? [...automationItems.options]
            : [
                'Lights',
                'Curtains',
                'AC',
                'TV',
                'Speakers',
                'Scene Settings',
              ];
        if (!opts.some((o) => /scene\s*settings?/i.test(String(o)))) {
          opts.push('Scene Settings');
        }
        return opts;
      })(),
    },
    {
      ...acTypes,
      type: 'checkboxGroup',
      label: acTypes.label || 'Air Condition Types',
      options:
        acTypes.options?.length > 0
          ? acTypes.options
          : ['Split', 'Cassette', 'Ductable'],
    },
    {
      ...approxWiring,
      type: 'text',
      label: approxWiring.label || 'Approx. Wiring Length',
    },
  ];

  const seen = new Set();
  return fields.filter((f) => {
    const id = String(f?.id || '')
      .toLowerCase()
      .replace(/_/g, '');
    if (!id) return true;
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });
};

/**
 * Foyer: after Light Categories, show a separate "Additional Electrical"
 * block with wall lights qty, console LED strip, AC wiring, speakers, automation.
 * Soft furnishing + paint are always kept.
 */
const splitFoyerAdditionalElectrical = (groups, template) => {
  const out = [];
  const pooledFields = [];
  let electricalCorePushed = false;
  let additionalInserted = false;

  const insertAdditional = () => {
    if (additionalInserted) return;
    out.push({
      id: 'additional_electrical',
      title: 'additional electrical',
      fields: buildFoyerAdditionalElectricalFields(pooledFields, template),
    });
    additionalInserted = true;
  };

  groups.forEach((group) => {
    // Always keep soft furnishing & paint as their own sections
    if (isSoftFurnishingOrPaintGroup(group)) {
      if (electricalCorePushed) insertAdditional();
      out.push(group);
      return;
    }

    // Fold entrance-bell / AC-only groups into Additional Electrical (avoid duplicates)
    if (isFoyerAdditionalOnlyGroup(group) && !isElectricalGroup(group)) {
      pooledFields.push(...(group.fields || []));
      return;
    }

    if (!isElectricalGroup(group)) {
      out.push(group);
      return;
    }

    const fields = group.fields || [];
    pooledFields.push(...fields);

    if (
      String(group.id || '')
        .toLowerCase()
        .includes('additional') ||
      String(group.title || '')
        .toLowerCase()
        .includes('additional')
    ) {
      return;
    }

    if (electricalCorePushed) return;

    const lightIdx = fields.findIndex(isLightCategoriesField);
    const coreFields = (
      lightIdx >= 0 ? fields.slice(0, lightIdx + 1) : fields.slice()
    ).filter((f) => !isFoyerAdditionalFieldId(f.id, f.label));
    const notesField = fields.find((f) => f.type === 'richNotes');
    if (notesField && !coreFields.some((f) => f.id === notesField.id)) {
      coreFields.push(notesField);
    }

    out.push({
      ...group,
      fields: coreFields,
    });
    electricalCorePushed = true;
  });

  insertAdditional();
  return ensureFoyerSoftFurnishingAndPaint(out, template);
};

const ensureFoyerSoftFurnishingAndPaint = (groups, template) => {
  const optionSets = template?.optionSets || template?.raw?.option_sets || {};
  const hasSoftFurnishing = groups.some((g) => {
    const id = String(g?.id || '').toLowerCase();
    const title = String(g?.title || '').toLowerCase();
    return id.includes('soft') || title.includes('soft furnish');
  });
  const hasPaint = groups.some((g) => {
    const id = String(g?.id || '').toLowerCase();
    const title = String(g?.title || '').toLowerCase();
    return id.includes('paint') || title.includes('paint');
  });

  const next = [...groups];

  if (!hasSoftFurnishing) {
    next.push({
      id: 'soft_furnishing',
      title: 'soft furnishing',
      fields: [
        {
          id: 'curtains',
          label: 'Curtains',
          type: 'singleSelect',
          options:
            optionSets.curtains ||
            optionSets.curtain_options || ['Yes', 'No', 'Sheer + Main'],
        },
        {
          id: 'window_covering',
          label: 'Window Covering',
          type: 'checkboxGroup',
          options:
            optionSets.window_covering ||
            optionSets.windowCovering || [
              'Blinds',
              'Roman Blinds',
              'Sheer Curtains',
            ],
        },
      ],
    });
  }

  if (!hasPaint) {
    next.push({
      id: 'paint',
      title: 'paint',
      fields: [
        {
          id: 'wall_paint',
          label: 'Wall Paint Options',
          type: 'checkboxGroup',
          options:
            optionSets.paint_options ||
            optionSets.wall_paint || [
              'Royale Shine',
              'PU',
              'Texture',
              'Royale Matt',
              'Satin',
              'Plastic Premium',
            ],
        },
        {
          id: 'ceiling_paint',
          label: 'Ceiling Paint Options',
          type: 'checkboxGroup',
          options:
            optionSets.paint_options ||
            optionSets.ceiling_paint || [
              'Royale Shine',
              'PU',
              'Texture',
              'Royale Matt',
              'Satin',
              'Plastic Premium',
            ],
        },
      ],
    });
  }

  // Prefer order: … electrical, additional electrical, soft furnishing, paint
  const soft = [];
  const paint = [];
  const rest = [];
  next.forEach((g) => {
    const id = String(g?.id || '').toLowerCase();
    const title = String(g?.title || '').toLowerCase();
    if (id.includes('soft') || title.includes('soft furnish')) soft.push(g);
    else if (id.includes('paint') || title.includes('paint')) paint.push(g);
    else rest.push(g);
  });

  const addIdx = rest.findIndex(
    (g) =>
      String(g?.id || '')
        .toLowerCase()
        .includes('additional_electrical') ||
      String(g?.title || '')
        .toLowerCase()
        .includes('additional electrical')
  );
  if (addIdx >= 0) {
    return [
      ...rest.slice(0, addIdx + 1),
      ...soft,
      ...paint,
      ...rest.slice(addIdx + 1),
    ];
  }

  return [...rest, ...soft, ...paint];
};

const findFieldByAliases = (fields, aliases, { excludeIds = [], fuzzy = false } = {}) => {
  const list = fields || [];
  const normalized = aliases.map((a) => String(a).toLowerCase());
  const excluded = excludeIds.map((a) => String(a).toLowerCase());

  const exact = list.find((f) => {
    const id = String(f.id || '').toLowerCase();
    return (
      !excluded.some((e) => id === e || id.includes(e)) &&
      normalized.some((a) => id === a)
    );
  });
  if (exact) return exact;

  const byExactLabel = list.find((f) => {
    const id = String(f.id || '').toLowerCase();
    const label = String(f.label || '').toLowerCase();
    if (excluded.some((e) => id.includes(e) || label.includes(e))) return false;
    return normalized.some((a) => label === a || label === a.replace(/_/g, ' '));
  });
  if (byExactLabel) return byExactLabel;

  if (!fuzzy) return null;

  return list.find((f) => {
    const id = String(f.id || '').toLowerCase().replace(/[\s-]/g, '_');
    const label = String(f.label || '').toLowerCase();
    if (excluded.some((e) => id.includes(e) || label.includes(e))) return false;
    return normalized.some((a) => {
      const phrase = a.replace(/_/g, ' ');
      return id.includes(a) || label.includes(phrase) || label.includes(a);
    });
  });
};

const ensureCheckboxField = (existing, id, label) =>
  existing
    ? {
        ...existing,
        type: 'checkbox',
        options: [],
        label: existing.label || label,
        visibleWhen: null,
      }
    : {
        id,
        label,
        type: 'checkbox',
        options: [],
      };

const buildFoyerAdditionalElectricalFields = (sourceFields = [], template) => {
  const optionSets = template?.optionSets || template?.raw?.option_sets || {};

  const wallLights = ensureCheckboxField(
    findFieldByAliases(
      sourceFields,
      ['wall_lights', 'walllights'],
      { excludeIds: ['qty', 'quantity', 'nos'] }
    ),
    'wall_lights',
    'Wall Lights'
  );

  const wallLightsQty =
    findFieldByAliases(sourceFields, [
      'wall_lights_qty',
      'walllightsqty',
      'wall_lights_quantity',
    ]) || {
      id: 'wall_lights_qty',
      label: 'Wall Lights (Qty)',
      type: 'number',
    };

  const consoleLed = ensureCheckboxField(
    findFieldByAliases(sourceFields, [
      'console_led_strip',
      'consoleledstrip',
      'console led',
    ]),
    'console_led_strip',
    'Console LED Strip'
  );

  const acWiring = ensureCheckboxField(
    findFieldByAliases(sourceFields, ['ac_wiring', 'acwiring', 'ac wiring']),
    'ac_wiring',
    'AC Wiring'
  );

  const speakers = ensureCheckboxField(
    findFieldByAliases(sourceFields, [
      'speakers_required',
      'speakersrequired',
      'speakers',
    ]),
    'speakers_required',
    'Speakers Required'
  );

  const automation = ensureCheckboxField(
    findFieldByAliases(sourceFields, [
      'automation_required',
      'automationrequired',
      'automation',
    ]),
    'automation_required',
    'Automation Required'
  );

  const automationItems =
    findFieldByAliases(sourceFields, [
      'automation_items',
      'automationitems',
      'automation items',
    ]) || null;

  const entranceBell =
    findFieldByAliases(sourceFields, [
      'entrance_bell',
      'entrancebell',
      'entrance bell',
    ]) || null;

  const fields = [
    wallLights,
    {
      ...wallLightsQty,
      id: wallLightsQty.id || 'wall_lights_qty',
      label: wallLightsQty.label || 'Wall Lights (Qty)',
      type: 'number',
      visibleWhen: { field: wallLights.id, equals: true },
    },
    consoleLed,
    acWiring,
    speakers,
    automation,
  ];

  if (automationItems) {
    fields.push({
      ...automationItems,
      visibleWhen: {
        field: automation.id,
        equals: true,
      },
    });
  }

  if (entranceBell) {
    fields.push(entranceBell);
  } else {
    const bellOpts =
      optionSets.entrance_bell ||
      optionSets.entranceBell || [
        'Normal Bell',
        'Smart Bell (WiFi)',
        'Bell With Video Screen / Digital Door Camera',
      ];
    fields.push({
      id: 'entrance_bell',
      label: 'Entrance Bell',
      type: 'checkboxGroup',
      options: bellOpts,
    });
  }

  const acTypes =
    findFieldByAliases(
      sourceFields,
      ['ac_types', 'actypes', 'air_condition_types', 'airconditiontypes'],
      { excludeIds: ['entrance', 'bell', 'wiring', 'approx'] }
    ) || {
      id: 'ac_types',
      label: 'Air Condition Types',
      type: 'checkboxGroup',
      options:
        optionSets.ac_types ||
        optionSets.air_condition_types || ['Split', 'Cassette', 'Ductable'],
    };

  const approxWiring =
    findFieldByAliases(sourceFields, [
      'approx_wiring_length',
      'approxwiringlength',
      'approx wiring',
      'wiring_length',
    ]) || {
      id: 'approx_wiring_length',
      label: 'Approx. Wiring Length',
      type: 'text',
    };

  fields.push(
    {
      ...acTypes,
      id: acTypes.id || 'ac_types',
      type: 'checkboxGroup',
      label: acTypes.label || 'Air Condition Types',
      options:
        acTypes.options?.length > 0
          ? acTypes.options
          : ['Split', 'Cassette', 'Ductable'],
    },
    {
      ...approxWiring,
      id: approxWiring.id || 'approx_wiring_length',
      type: 'text',
      label: approxWiring.label || 'Approx. Wiring Length',
    }
  );

  // One Entrance Bell only, always before Air Condition Types
  const seen = new Set();
  return fields.filter((f) => {
    const id = String(f?.id || '')
      .toLowerCase()
      .replace(/_/g, '');
    if (!id) return true;
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });
};

const FALSE_CEILING_GATE_IDS = new Set([
  'false_ceiling_required',
  'false_ceiling',
  'required',
]);

const FALSE_CEILING_DEPENDENT_IDS = new Set([
  'false_ceiling_type',
  'types',
  'cove_lighting',
  'cove_lighting_options',
  'coveLighting',
  'ceiling_design',
  'ceiling_design_options',
  'ceilingDesign',
]);

const isFalseCeilingGroup = (group, fields) => {
  const id = String(group?.id || '').toLowerCase();
  if (id.includes('false_ceiling') || id.includes('falseceiling')) return true;
  const title = String(group?.title || '').toLowerCase();
  if (title.includes('false ceiling')) return true;
  return (fields || []).some(
    (f) =>
      FALSE_CEILING_DEPENDENT_IDS.has(f.id) ||
      FALSE_CEILING_GATE_IDS.has(f.id) ||
      /false.?ceiling/i.test(f.label || '')
  );
};

/**
 * PDF / UX: False Ceiling Required is a checkbox; type / cove / design
 * only appear when it is checked.
 */
const enrichFalseCeilingFields = (group, fields = [], template = null) => {
  if (!isFalseCeilingGroup(group, fields)) return fields;

  const optionSets = template?.optionSets || template?.raw?.option_sets || {};
  let next = [...fields];

  let gate =
    next.find((f) => FALSE_CEILING_GATE_IDS.has(f.id)) ||
    next.find((f) => /required/i.test(f.id) || /required/i.test(f.label || ''));

  if (!gate) {
    gate = {
      id: 'false_ceiling_required',
      label: 'False Ceiling Required',
      type: 'checkbox',
      options: [],
      required: false,
    };
    next = [gate, ...next];
  } else {
    gate = {
      ...gate,
      type: 'checkbox',
      label: /required/i.test(gate.label || '')
        ? gate.label
        : 'False Ceiling Required',
      options: [],
      visibleWhen: null,
    };
    next = next.map((f) => (f.id === gate.id ? gate : f));
  }

  const ensureMulti = (id, label, optionsRefs, fallbackOptions) => {
    if (next.some((f) => f.id === id || optionsRefs.includes(f.id))) return;
    const refs = Array.isArray(optionsRefs) ? optionsRefs : [optionsRefs];
    let options = fallbackOptions;
    for (const ref of refs) {
      if (optionSets[ref]?.length) {
        options = optionSets[ref];
        break;
      }
    }
    next.push({
      id,
      label,
      type: 'checkboxGroup',
      options,
      required: false,
    });
  };

  ensureMulti(
    'false_ceiling_type',
    'False Ceiling Type',
    ['false_ceiling_type'],
    ['POP', 'Wooden', 'Stretch', 'Grid']
  );
  ensureMulti(
    'cove_lighting',
    'Cove Lighting Options',
    ['cove_lighting', 'cove_lighting_options'],
    ['Outside Cove Only', 'Inside Cove Only', 'Inside + Outside Cove']
  );
  ensureMulti(
    'ceiling_design',
    'Ceiling Design Options',
    ['ceiling_design', 'ceiling_design_options'],
    ['Grooves', 'Mouldings', 'Beam Covering', 'No Design']
  );

  return next.map((field) => {
    if (field.id === gate.id) return field;
    if (field.type === 'richNotes') return field;
    const isDependent =
      FALSE_CEILING_DEPENDENT_IDS.has(field.id) ||
      /type|cove|design/i.test(field.id) ||
      /type|cove|design/i.test(field.label || '');
    if (!isDependent) return field;
    return {
      ...field,
      visibleWhen: {
        field: gate.id,
        equals: true,
      },
    };
  });
};

/** All program field groups (A/B/C/…) for preview or a single program step. */
export const getProgramFieldsBySection = (template, onlyLetter = null) => {
  const fields = template?.programFields || [];
  const map = new Map();
  fields.forEach((raw) => {
    const field = resolveTemplateQuestion(raw, template);
    if (!field) return;
    const section = String(raw.section || 'A').toUpperCase();
    if (onlyLetter && section !== String(onlyLetter).toUpperCase()) return;
    if (!map.has(section)) map.set(section, []);
    map.get(section).push({ ...field, section });
  });
  return Array.from(map.entries())
    .sort((a, b) => letterSortKey(a[0]) - letterSortKey(b[0]))
    .map(([section, sectionFields]) => ({
      id: `program-${section}`,
      title: getPdfTitleForLetter(section),
      letter: section,
      fields: sectionFields,
    }));
};

export const emptyAnswersForGroups = (resolvedGroups) => {
  const answers = {};
  (resolvedGroups || []).forEach((group) => {
    (group.fields || []).forEach((field) => {
      answers[field.id] = defaultValueForTemplateField(field);
    });
  });
  return answers;
};

const addNumberFieldId = (ids, q) => {
  const type = String(q?.type || '').toLowerCase();
  const qid = q?.id || q?.question_id;
  if (!qid) return;
  const id = String(qid);
  if (isStringNotNumberFieldId(id)) return;
  if (type === 'number' || type === 'integer' || type === 'float' || isApiNumberFieldId(id)) {
    ids.add(id);
  }
};

/** Collect template question ids with type number (program + all rooms). */
export const collectNumberFieldIds = (template = null) => {
  const ids = new Set(PROGRAM_NUMBER_IDS);
  (template?.programFields || []).forEach((field) => {
    addNumberFieldId(ids, field);
  });
  (template?.roomTemplates || []).forEach((rt) => {
    (rt.groups || []).forEach((groupRef) => {
      const resolved = resolveGroup(groupRef, template);
      (resolved.questions || []).forEach((q) => {
        addNumberFieldId(ids, q);
      });
    });
  });
  return ids;
};

/** Number field ids for one room type. */
export const collectNumberFieldIdsForRoomType = (template, roomType) => {
  const ids = new Set();
  const roomTemplate = (template?.roomTemplates || []).find(
    (rt) => rt?.room_type === roomType
  );
  if (!roomTemplate) return ids;

  (roomTemplate.groups || []).forEach((groupRef) => {
    const resolved = resolveGroup(groupRef, template);
    (resolved.questions || []).forEach((q) => {
      addNumberFieldId(ids, q);
    });
  });
  return ids;
};

/** Coerce typed program fields for PATCH; overwrite stored "" with numbers. */
export const collectKnownProgramFieldIds = (template = null) => {
  const ids = new Set(PROGRAM_NUMBER_IDS);
  (template?.programFields || []).forEach((raw) => {
    const id = raw?.id || raw?.question_id;
    if (id) ids.add(String(id));
  });
  // Common ultimate-scope program keys even if template omits them
  [
    'project_address',
    'client_names',
    'property_type',
    'unit_type',
    'total_carpet_area',
    'services_required',
    'deliverables',
  ].forEach((id) => ids.add(id));
  return ids;
};

/** Option sets for program single/multi fields. */
export const collectProgramOptionMap = (template = null) => {
  const map = new Map();
  (template?.programFields || []).forEach((raw) => {
    const field = resolveTemplateQuestion(raw, template);
    if (!field?.id) return;
    if (Array.isArray(field.options) && field.options.length > 0) {
      map.set(
        field.id,
        new Set(field.options.map((o) => normalizeOptionLabel(String(o))))
      );
    }
  });
  return map;
};

/**
 * Guide sample fields (project_name, budget, …) and other unknowns must not
 * be PATCHed — only template program ids + required count numbers.
 */
export const sanitizeProgramForApi = (program = {}, template = null) => {
  const numberIds = collectNumberFieldIds(template);
  const knownIds = collectKnownProgramFieldIds(template);
  const optionMap = collectProgramOptionMap(template);

  const out = {};
  Object.entries(program || {}).forEach(([key, value]) => {
    if (!knownIds.has(key) && !numberIds.has(key) && !isApiNumberFieldId(key)) {
      return;
    }

    if (optionMap.has(key)) {
      const allowed = optionMap.get(key);
      let nextVal = value;
      if (typeof nextVal === 'number') {
        nextVal =
          nextVal === 0 || isEmptyAnswerNumber(nextVal)
            ? ''
            : String(nextVal);
      }
      if (Array.isArray(nextVal)) {
        const filtered = nextVal
          .map((v) => canonicalizeOptionValue(v, allowed))
          .filter(Boolean);
        if (!filtered.length) return;
        if (isSingleSelectAnswerId(key)) {
          out[key] = filtered[0];
          return;
        }
        out[key] = filtered;
        return;
      }
      if (nextVal == null || nextVal === '') return;
      const canonical = canonicalizeOptionValue(nextVal, allowed);
      if (!canonical) return;
      out[key] = canonical;
      return;
    }

    if (numberIds.has(key) || isApiNumberFieldId(key)) {
      out[key] = coerceToApiNumber(value);
      return;
    }

    // Multi arrays (services_required / deliverables) — keep string arrays
    if (Array.isArray(value)) {
      out[key] = value
        .map((v) => (v == null ? '' : String(v).trim()))
        .filter(Boolean);
      return;
    }

    out[key] = value;
  });

  // Always send known program number keys so stored "" is overwritten.
  PROGRAM_NUMBER_IDS.forEach((key) => {
    out[key] = coerceToApiNumber(out[key] ?? program?.[key]);
  });
  return out;
};

/** Map guide/legacy area keys onto ultimate-scope room_type ids. */
export const CANONICAL_AREA_KEYS = {
  living_room: 'living_drawing',
  living: 'living_drawing',
  drawing: 'living_drawing',
  dining_area: 'dining',
  foyer_area: 'foyer',
  kitchen_area: 'kitchen',
};

export const canonicalizeAreaKey = (roomType = '') => {
  const key = String(roomType || '');
  return CANONICAL_AREA_KEYS[key] || key;
};

/** Keep only template area room types; remap legacy aliases. */
export const normalizeAreasPayload = (areas = {}, template = null) => {
  const repeating = new Set([
    'bedroom_suite',
    'balcony',
    'help_room',
    'store_room',
  ]);
  const allowed = new Set(
    (template?.roomTemplates || [])
      .map((rt) => rt?.room_type)
      .filter((rt) => rt && !repeating.has(rt))
  );
  // Static areas always allowed even if template list is empty during boot
  [
    'main_entrance',
    'foyer',
    'living_drawing',
    'dining',
    'kitchen',
  ].forEach((k) => allowed.add(k));

  const out = {};
  Object.entries(areas || {}).forEach(([roomType, area]) => {
    const canonical = canonicalizeAreaKey(roomType);
    if (repeating.has(canonical)) return;
    if (allowed.size > 0 && !allowed.has(canonical)) return;
    // Prefer first write; merge if alias + canonical both present
    const prev = out[canonical];
    if (!prev) {
      out[canonical] = {
        ...(area || {}),
        room_type: canonical,
      };
      return;
    }
    out[canonical] = {
      ...prev,
      ...(area || {}),
      room_type: canonical,
      answers: {
        ...(prev.answers || {}),
        ...(area?.answers || {}),
      },
      notes: area?.notes || prev.notes,
    };
  });
  return out;
};

/** Question ids that FE may have invented — never send to API. */
export const FE_ONLY_ANSWER_KEYS = new Set([
  'ac_types',
  'actypes',
  // fans / ac_wiring / *_qty are real template fields on rooms — do not drop
  'speakers_required',
  'corner_unit',
  'corner_unit_qty',
  'tandem_drawers',
  'tandem_drawers_qty',
  'bottle_pullout',
  'bottle_pullout_qty',
  // Stage rejects these as unknown on areas (not in ultimate-scope contract)
  'balcony_door_width',
  'balcony_door_height',
]);

/**
 * Room-type-specific ids that Stage rejects as unknown question_id.
 * living_drawing may own balcony_door_*; kitchen must not.
 * dining owns dining_chairs_qty / dining_table_persons — do not forbid there.
 */
export const FORBIDDEN_ANSWERS_BY_ROOM_TYPE = {
  kitchen: [
    'balcony_door_width',
    'balcony_door_height',
    'corner_unit',
    'corner_unit_qty',
    'tandem_drawers',
    'tandem_drawers_qty',
    'bottle_pullout',
    'bottle_pullout_qty',
    'dining_chairs_qty',
    'dining_table_persons',
  ],
  kitchen_area: [
    'balcony_door_width',
    'balcony_door_height',
    'corner_unit',
    'corner_unit_qty',
    'tandem_drawers',
    'tandem_drawers_qty',
    'bottle_pullout',
    'bottle_pullout_qty',
    'dining_chairs_qty',
    'dining_table_persons',
  ],
  foyer: [
    'dining_chairs_qty',
    'dining_table_persons',
    'balcony_door_width',
    'balcony_door_height',
  ],
  foyer_area: [
    'dining_chairs_qty',
    'dining_table_persons',
    'balcony_door_width',
    'balcony_door_height',
  ],
  main_entrance: [
    'dining_chairs_qty',
    'dining_table_persons',
    'balcony_door_width',
    'balcony_door_height',
  ],
  living_drawing: [
    'dining_chairs_qty',
    'dining_table_persons',
    'balcony_door_width',
    'balcony_door_height',
  ],
  living: [
    'dining_chairs_qty',
    'dining_table_persons',
    'balcony_door_width',
    'balcony_door_height',
  ],
  drawing: [
    'dining_chairs_qty',
    'dining_table_persons',
    'balcony_door_width',
    'balcony_door_height',
  ],
};

export const isForbiddenAnswerForRoomType = (roomType, questionId) => {
  const id = String(questionId || '');
  if (!id) return false;
  if (FE_ONLY_ANSWER_KEYS.has(id)) return true;
  const list = FORBIDDEN_ANSWERS_BY_ROOM_TYPE[roomType] || [];
  return list.includes(id);
};

/**
 * Drop FE-invented / room-forbidden keys from an answers map.
 * IMPORTANT: do NOT send null — Stage treats unknown question_id as 400
 * even when the value is null.
 */
export const finalizeAnswersForRoomType = (roomType, answers = {}) => {
  const out = {};
  Object.entries(answers || {}).forEach(([key, value]) => {
    if (value === null || value === undefined) return;
    if (isForbiddenAnswerForRoomType(roomType, key)) return;
    if (FE_ONLY_ANSWER_KEYS.has(key)) return;
    out[key] = value;
  });
  return out;
};

/** Strip globally never-send keys (all room types). */
export const stripNeverSendAnswers = (answers = {}) => {
  const out = {};
  Object.entries(answers || {}).forEach(([key, value]) => {
    if (value === null || value === undefined) return;
    if (FE_ONLY_ANSWER_KEYS.has(key)) return;
    out[key] = value;
  });
  return out;
};

/** Question ids declared on a room template (API-known only — not FE UI extras). */
export const collectKnownQuestionIdsForRoomType = (template, roomType) => {
  const ids = new Set();
  const roomTemplate = (template?.roomTemplates || []).find(
    (rt) => rt?.room_type === roomType
  );
  if (!roomTemplate) {
    // Still allow contract singles when template boot is incomplete
    if (roomType === 'bedroom_suite') ids.add('wardrobe_height');
    if (roomType === 'kitchen' || roomType === 'kitchen_area') {
      ids.add('hardware_level');
    }
    return ids;
  }

  // RAW template ids only. Enriched UI fields (corner_unit_qty, balcony_door_*, …)
  // must never become "known" or forceAnswerNumbers will invent unknown question_ids.
  (roomTemplate.groups || []).forEach((groupRef) => {
    const resolvedGroup = resolveGroup(groupRef, template);
    (resolvedGroup.questions || []).forEach((q) => {
      const qid = q?.id || q?.question_id;
      if (!qid) return;
      const id = String(qid);
      if (FE_ONLY_ANSWER_KEYS.has(id)) return;
      if (isForbiddenAnswerForRoomType(roomType, id)) return;
      ids.add(id);
    });
  });

  // Contract fields that must survive sanitize even if template omits them
  if (roomType === 'bedroom_suite') ids.add('wardrobe_height');
  if (roomType === 'kitchen' || roomType === 'kitchen_area') {
    ids.add('hardware_level');
  }

  // Never keep FE-only / room-forbidden ids even if template listed them wrongly
  [...ids].forEach((id) => {
    if (FE_ONLY_ANSWER_KEYS.has(id) || isForbiddenAnswerForRoomType(roomType, id)) {
      ids.delete(id);
    }
  });

  return ids;
};

/** Allowed option values per question id for a room type. */
export const collectQuestionOptionsForRoomType = (template, roomType) => {
  const map = new Map();
  const roomTemplate = (template?.roomTemplates || []).find(
    (rt) => rt?.room_type === roomType
  );
  if (!roomTemplate) return map;

  const addFieldOptions = (field) => {
    if (!field?.id) return;
    let opts = Array.isArray(field.options) ? field.options : [];
    if (!opts.length && field.options_ref) {
      const optionSets =
        template?.optionSets || template?.raw?.option_sets || {};
      opts = optionSets[field.options_ref] || [];
    }
    if (!opts.length) return;
    map.set(
      field.id,
      new Set(opts.map((o) => normalizeOptionLabel(String(o))))
    );
  };

  // Enriched UI fields include FE defaults that must match the API contract.
  const resolved = getResolvedFieldsForRoomTemplate(roomTemplate, template);
  resolved.forEach((group) => {
    (group.fields || []).forEach(addFieldOptions);
  });

  // Guarantee contract options even if template omit options_ref.
  if (roomType === 'bedroom_suite' && !map.has('wardrobe_height')) {
    map.set(
      'wardrobe_height',
      new Set(['Full Height', '8 ft', '8 ft + Loft'])
    );
  }
  if (
    (roomType === 'kitchen' || roomType === 'kitchen_area') &&
    !map.has('hardware_level')
  ) {
    map.set(
      'hardware_level',
      new Set([
        'Basic (Local Indian Brands)',
        'Mid (Hettich India / Ozone / Inox)',
        'Premium (Hafele / Hettich Germany)',
        'Ultra Premium (Blum Soft-Close)',
      ])
    );
  }

  if (map.size > 0) return map;

  (roomTemplate.groups || []).forEach((groupRef) => {
    const resolvedGroup = resolveGroup(groupRef, template);
    (resolvedGroup.questions || []).forEach((q) => {
      const field = resolveTemplateQuestion(
        q?.id ? q : { ...q, id: q?.question_id || q?.id },
        template
      );
      addFieldOptions(field);
    });
  });
  return map;
};

/** Options FE previously invented for AC type (not always valid in template). */
const FE_INVENTED_AC_OPTIONS = new Set(['Split', 'Cassette', 'Ductable']);

/**
 * Drop unknown question ids, coerce numbers, and filter invalid choice values.
 */
export const sanitizeAnswersForApi = (
  answers = {},
  knownIds = null,
  optionMap = null,
  numberIds = null
) => {
  const out = {};
  Object.entries(answers || {}).forEach(([key, value]) => {
    if (FE_ONLY_ANSWER_KEYS.has(key)) return;
    if (knownIds && knownIds.size > 0 && !knownIds.has(key)) return;

    // Choice fields first — never coerce option values to numbers.
    // Exception: template may attach options_ref to dimension fields; if the
    // value is empty/numeric, prefer JSON number (submit requires numbers).
    if (optionMap && optionMap.has(key)) {
      if (
        isApiNumberFieldId(key) &&
        !isStringNotNumberFieldId(key) &&
        !looksLikeOptionLabel(value) &&
        !(Array.isArray(value) && value.some(looksLikeOptionLabel))
      ) {
        out[key] = coerceAnswerNumberValue(value);
        return;
      }
      const allowed = optionMap.get(key);
      let nextVal = value;
      // Undo accidental number coerce (e.g. wardrobe_height → 0)
      if (typeof nextVal === 'number') {
        nextVal =
          nextVal === 0 || isEmptyAnswerNumber(nextVal)
            ? ''
            : String(nextVal);
      }

      if (Array.isArray(nextVal)) {
        const filtered = nextVal
          .map((v) => canonicalizeOptionValue(v, allowed))
          .filter(Boolean);
        if (!filtered.length) return;
        // Single-select contract: send exact option string, not ["…"]
        if (isSingleSelectAnswerId(key)) {
          out[key] = filtered[0];
          return;
        }
        out[key] = filtered;
        return;
      }

      // Omit empty / invalid options so we don't overwrite with 0 later
      if (nextVal == null || nextVal === '') return;
      if (typeof nextVal === 'object') return;
      const canonical = canonicalizeOptionValue(nextVal, allowed);
      if (!canonical) return;
      out[key] = canonical;
      return;
    }

    // Without optionMap, still unwrap known single-selects from arrays
    // and drop values that clearly aren't numbers for choice ids.
    if (isSingleSelectAnswerId(key)) {
      let nextVal = value;
      if (typeof nextVal === 'number') {
        nextVal =
          nextVal === 0 || isEmptyAnswerNumber(nextVal)
            ? ''
            : String(nextVal);
      }
      if (Array.isArray(nextVal)) {
        nextVal = nextVal.find((v) => v != null && String(v).trim()) ?? '';
      }
      if (nextVal == null || nextVal === '') return;
      if (typeof nextVal === 'object') return;
      out[key] = normalizeOptionLabel(nextVal);
      return;
    }

    if (isStringNotNumberFieldId(key)) {
      if (typeof value === 'number') {
        out[key] = value === 0 ? '' : coerceToApiString(value);
      } else {
        out[key] = coerceToApiString(value ?? '');
      }
      return;
    }

    if (
      (numberIds && numberIds.has(key) && !isStringNotNumberFieldId(key)) ||
      isApiNumberFieldId(key)
    ) {
      out[key] = coerceAnswerNumberValue(value);
      return;
    }

    const isAcTypeKey = /^ac_types?$/i.test(key) || /air_condition/i.test(key);

    if (isAcTypeKey) {
      if (Array.isArray(value)) {
        out[key] = value.filter(
          (v) => !FE_INVENTED_AC_OPTIONS.has(String(v))
        );
        return;
      }
      if (FE_INVENTED_AC_OPTIONS.has(String(value))) return;
    }

    out[key] = value;
  });

  // Force every known number id onto the payload so stored "" is replaced.
  // Skip string-typed fields; option-mapped dimension fields already coerced above.
  if (numberIds && numberIds.size > 0) {
    numberIds.forEach((id) => {
      if (isStringNotNumberFieldId(id)) return;
      if (isSingleSelectAnswerId(id)) return;
      if (optionMap && optionMap.has(id) && !isApiNumberFieldId(id)) return;
      if (
        optionMap &&
        optionMap.has(id) &&
        looksLikeOptionLabel(out[id] ?? answers?.[id])
      )
        return;
      if (isApiNumberFieldId(id) || (numberIds && numberIds.has(id))) {
        if (optionMap && optionMap.has(id) && looksLikeOptionLabel(out[id]))
          return;
        out[id] = coerceAnswerNumberValue(out[id] ?? answers?.[id]);
      }
    });
  }

  // Also force from the full known question set (covers washroom_*, wardrobe_*, …)
  if (knownIds && knownIds.size > 0) {
    knownIds.forEach((id) => {
      if (isStringNotNumberFieldId(id)) return;
      if (isSingleSelectAnswerId(id)) return;
      if (
        optionMap &&
        optionMap.has(id) &&
        looksLikeOptionLabel(out[id] ?? answers?.[id])
      )
        return;
      if (optionMap && optionMap.has(id) && !isApiNumberFieldId(id)) return;
      if (!isApiNumberFieldId(id)) return;
      out[id] = coerceAnswerNumberValue(out[id] ?? answers?.[id]);
    });
  }

  COMMON_ANSWER_NUMBER_IDS.forEach((id) => {
    if (isStringNotNumberFieldId(id)) return;
    if (isSingleSelectAnswerId(id)) return;
    if (
      optionMap &&
      optionMap.has(id) &&
      looksLikeOptionLabel(out[id] ?? answers?.[id])
    ) {
      return;
    }
    if (
      Object.prototype.hasOwnProperty.call(out, id) ||
      Object.prototype.hasOwnProperty.call(answers || {}, id) ||
      (knownIds && knownIds.has(id)) ||
      (numberIds && numberIds.has(id))
    ) {
      out[id] = coerceAnswerNumberValue(out[id] ?? answers?.[id]);
    }
  });

  return coerceAnswerStrings(out);
};
